import { useCallback, useEffect, useRef, useState, Fragment, useMemo } from 'react'
import {
  Card,
  Typography,
  Stack,
  ImageList,
  ImageListItem,
  ListSubheader,
  ImageListItemBar,
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  AvatarGroup,
  Tooltip,
  CircularProgress,
  Avatar,
  Snackbar,
  IconButton,
  makeStyles,
  Skeleton,
  Checkbox,
  Chip,
  Badge,
  Tabs,
  Tab,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core'
import { useTheme, withStyles } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { ThumbUpAltOutlined, ThumbDownOffAltOutlined, CancelOutlined, Link, Notes, Description, PictureAsPdf, CalendarToday, Event, Fullscreen, UploadFile } from '@material-ui/icons'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import EditIcon from '@material-ui/icons/Edit'
import { sentenceCase } from 'change-case'
import moment from 'moment'
import { formatDateOnly } from 'src/utils/dateOnly'
import Scrollbar from 'src/components/Scrollbar'
import { REST_API, REST_API_END_POINT } from 'src/constants/Defaultvalues'
import { useSnackbar } from 'notistack'
import axios from 'axios'
import { LoadingButton } from '@material-ui/lab'
import { UserMoreMenu } from '../user/list'
import Label from 'src/components/Label'
import Modal from '../blog/ProjectModel'
import { AccountNotifications } from '../user/account'
import { empty } from 'src/Helpers/Common'
import { resolveAttachmentProxyPath } from 'src/Helpers/FileDownloadUtils'
import { fUSPrice } from 'src/utils/formatNumber'
import { Icon } from '@iconify/react'
import arrowLeftOutlined from '@iconify/icons-ant-design/arrow-left-outlined';
import arrowRightOutlined from '@iconify/icons-ant-design/arrow-right-outlined';
import emailFillIcon from '@iconify/icons-eva/email-fill';
import SendDesignsEmailModal from './SendDesignsEmailModal';
import jsPDF from 'jspdf';
import PDFEditor from './PDFEditor'
import AttachmentDownloadIconButton from './AttachmentDownloadIconButton'
import AttachmentDownloadAllButton from './AttachmentDownloadAllButton'
import AttachmentPreviewDialog from './AttachmentPreviewDialog'
import { UploadMultiFile } from 'src/components/upload'
import { supabase } from '../../../supabase/supabaseClient'
import { getFileExtension, isOfficeFile as isOfficeAttachment, resolveDownloadFileName } from 'src/Helpers/FileDownloadUtils'
import { getGroupLosingMoneyFlags, getGroupInstallNotesDisplay } from 'src/pages/dashboard/quotation/quotationGroupingHelpers'
import { buildGroupedDesignOrderDisplayRows } from 'src/utils/designOrderInvoiceUtils'

const sortDesignOrderProductsByIndex = (products) =>
  [...(products || [])].sort(
    (a, b) =>
      (Number(a?.product_index) || 0) - (Number(b?.product_index) || 0) ||
      (Number(a?.id) || 0) - (Number(b?.id) || 0),
  )

const getDesignOrderProductGroupId = (product) =>
  product?.group_id ?? product?.customer_order_product_group_id ?? product?.groupId ?? null

const resolveDesignOrderDetailsGroupDisplay = (item, groupMetaById) => {
  const groupId = getDesignOrderProductGroupId(item)
  if (!groupId) return null
  const group = groupMetaById?.get(String(groupId))
  const groupName =
    group?.group_name ||
    group?.name ||
    item?.group_name ||
    'Group'
  return { groupId, groupName }
}

const mapDesignOrderDetailsProductAttachmentForDisplay = (file, item, groupMetaById) => {
  const groupDisplay = resolveDesignOrderDetailsGroupDisplay(item, groupMetaById)
  const productKey = item?.id || item?.product_index
  const productName = item?.name || item?.product_name || `Product ${item?.product_index || ''}`

  const base = {
    ...file,
    url: file?.url || file?.name || file?.file_url,
    name: file?.name || file?.url || file?.file_url,
    location_ids: file?.location_ids || String(productKey || ''),
    _productAttachment: true,
    product_index_id: file?.product_index_id ?? productKey,
  }

  if (groupDisplay) {
    return {
      ...base,
      productNames: [{ product_id: groupDisplay.groupId, product_name: groupDisplay.groupName }],
      _groupAttachment: true,
    }
  }

  return {
    ...base,
    productNames:
      file?.productNames ||
      (productKey
        ? [{ product_id: productKey, product_name: productName }]
        : []),
    _groupAttachment: false,
  }
}

const getDesignOrderDetailsCatalogLinkLabel = (product, groupMetaById) => {
  const groupDisplay = resolveDesignOrderDetailsGroupDisplay(product, groupMetaById)
  if (groupDisplay) return groupDisplay.groupName
  return product?.product_notes || product?.name || product?.product_name || '-'
}

const ATTACHMENTS_PUBLIC_MARKER = '/storage/v1/object/public/signwise-attachments/'

const normalizeUrlString = (raw) => {
  if (!raw) return ''
  try {
    const u = new URL(raw)
    return `${u.origin}${u.pathname}`.replace(/\/+$/, '')
  } catch {
    return raw.split('?')[0].split('#')[0].trim()
  }
}

/** True for URLs / storage paths — false for bare filenames like "Screenshot.png". */
const looksLikeAttachmentRef = (raw) => {
  const s = String(raw || '')
  if (!s) return false
  if (
    s.includes('://') ||
    s.includes('/attachments/file') ||
    s.includes('/storage/v1/object/') ||
    s.includes(ATTACHMENTS_PUBLIC_MARKER)
  ) {
    return true
  }
  // storage-relative path e.g. sales-orders/123_file.png
  return s.includes('/') && /\.[a-z0-9]+$/i.test(s)
}

/** Collapse proxy URLs + direct Supabase URLs to the same storage path. */
const normalizeAttachmentIdentifier = (raw) => {
  if (!raw) return ''
  const str = String(raw).trim()
  if (!str) return ''

  if (str.includes('/attachments/file') && str.includes('path=')) {
    return normalizeUrlString(resolveAttachmentProxyPath(str))
  }

  const publicIdx = str.indexOf(ATTACHMENTS_PUBLIC_MARKER)
  if (publicIdx !== -1) {
    return normalizeUrlString(str.slice(publicIdx + ATTACHMENTS_PUBLIC_MARKER.length))
  }

  return normalizeUrlString(str)
}

const getAttachmentId = (file) =>
  file?.id ?? file?.ID ?? file?.attachment_id ?? file?.design_order_attachment_id ?? null

/** Prefer url/file_url; only use name when it is a URL/path (not a bare filename). */
const getAttachmentPrimaryRef = (f) => {
  if (!f) return ''
  if (f.url) return f.url
  if (f.file_url) return f.file_url
  if (looksLikeAttachmentRef(f.name)) return f.name
  return ''
}

const attachmentFileKey = (f) => {
  const primary = getAttachmentPrimaryRef(f)
  if (primary) return normalizeAttachmentIdentifier(primary)
  // last resort: bare name (unique only when filenames differ)
  return normalizeAttachmentIdentifier(f?.name || '')
}

const attachmentMatches = (a, b) => {
  const idA = getAttachmentId(a)
  const idB = getAttachmentId(b)
  if (idA != null && idB != null && String(idA) !== '' && String(idB) !== '') {
    return String(idA) === String(idB)
  }

  const keyA = attachmentFileKey(a)
  const keyB = attachmentFileKey(b)
  return Boolean(keyA && keyB && keyA === keyB)
}

const getDesignOrderProductUnitPrice = (item) =>
  Number(item?.actual_retail_price ?? item?.retail_price ?? item?.price ?? 0)

const getDesignOrderProductQty = (item) =>
  Number(item?.quantity ?? item?.locationQuantity ?? 1)

const getDesignOrderProductSubtotal = (item) => {
  if (item?.product_sub_total != null) return Number(item.product_sub_total)
  const unit = getDesignOrderProductUnitPrice(item)
  const qty = getDesignOrderProductQty(item)
  return unit * qty
}

const getDesignOrderProductGrandTotal = (item) => {
  const installCost = parseFloat(item?.install_cost) || 0
  const subtotal = getDesignOrderProductSubtotal(item)
  if (installCost > 0) {
    const installQty = Number(item?.install_quantity ?? 1)
    return subtotal + installCost * installQty
  }
  if (item?.product_total != null) return Number(item.product_total)
  return subtotal
}

const getDesignOrderProductInstallTotal = (item) => {
  const installCost = parseFloat(item?.install_cost) || 0
  const installQty = Number(item?.install_quantity ?? item?.install_qty ?? 1)
  return installCost > 0 ? installCost * installQty : 0
}

const getDesignOrderServiceTotal = (service) => {
  if (service?.total != null) return Number(service.total) || 0
  if (service?.total_cost != null) return Number(service.total_cost) || 0
  return (Number(service?.unit_cost) || 0) * (Number(service?.quantity) || 1)
}

const getDesignOrderServiceUnitCost = (service) => {
  if (service?.unit_cost != null) return Number(service.unit_cost) || 0
  const qty = Number(service?.quantity) || 1
  return qty ? getDesignOrderServiceTotal(service) / qty : 0
}

const getDesignOrderServiceQuantity = (service) =>
  Number(service?.quantity ?? service?.qty ?? 1) || 0

const designOrderProductShowsPricing = (item) =>
  item?.is_product_row !== 1 ||
  getDesignOrderProductUnitPrice(item) > 0 ||
  item?.product_sub_total != null ||
  item?.product_total != null

const getDesignOrderProductFormulaEach = (item) => {
  if (item?.inhouse === 0) {
    return Number(item?.sub_contract_price || 0)
  }
  const fromMarkup = Number(
    (item?.expected_material_cost_with_markup || 0) +
      (item?.expected_service_cost_with_markup || 0),
  )
  if (fromMarkup > 0) return fromMarkup
  return Number(item?.unit_cost ?? item?.expected_formula_price ?? item?.expected_cost ?? 0)
}

const isNegativeMarketingAdjMode = (adjValueType) =>
  adjValueType === false ||
  adjValueType === 0 ||
  adjValueType === '0' ||
  adjValueType === 'false'

const getDesignOrderProductLosingMoneyFlags = (item) => {
  if (!item) {
    return { isProductLosingMoney: false, isInstallLosingMoney: false, isLosingMoneyItem: false }
  }
  const productFormulaEach = getDesignOrderProductFormulaEach(item)
  const productRetailEach = Math.max(
    0,
    Number(item?.actual_retail_price ?? item?.retail_price ?? item?.product_retail_price ?? 0),
  )
  const isProductLosingMoney =
    isNegativeMarketingAdjMode(item?.adj_value_type) &&
    productRetailEach < productFormulaEach

  const installFormulaEach = Number(item?.expected_install_labor_cost_with_markup || 0)
  const installRetailEach = Math.max(0, Number(item?.install_cost || 0))
  const isInstallLosingMoney =
    installRetailEach > 0 &&
    isNegativeMarketingAdjMode(item?.install_adj_value_type) &&
    installRetailEach < installFormulaEach

  return {
    isProductLosingMoney,
    isInstallLosingMoney,
    isLosingMoneyItem: isProductLosingMoney || isInstallLosingMoney,
  }
}

const getDesignOrderDetailsGroupLosingMoneyFlags = (row) => {
  const groupMeta = row?.groupMeta || {}
  return getGroupLosingMoneyFlags({
    id: row?.groupId,
    groupAdjValueType:
      groupMeta.product_marketing_adj_value_type ?? groupMeta.productMarketingAdjValueType,
    groupInstallAdjValueType:
      groupMeta.install_marketing_adj_value_type ?? groupMeta.installMarketingAdjValueType,
    baseProductFormulaEach:
      groupMeta.product_total_formula_each ?? groupMeta.productTotalFormulaEach,
    baseInstallFormulaEach:
      groupMeta.install_total_formula_each ?? groupMeta.installTotalFormulaEach,
    adjustedGroupRetailEach: row?.adjustedGroupRetailEach,
    adjustedGroupInstallRetailEach: row?.adjustedGroupInstallRetailEach,
    totalProductFormulaEach:
      groupMeta.product_total_formula_each ?? groupMeta.productTotalFormulaEach,
    totalInstallFormulaEach:
      groupMeta.install_total_formula_each ?? groupMeta.installTotalFormulaEach,
  })
}

const getDesignOrderLosingMoneyRowSx = (theme, isLosingMoneyItem) =>
  isLosingMoneyItem
    ? {
      '& .MuiTableCell-root, & .MuiTableCell-root .MuiInputBase-input, & .MuiTableCell-root .MuiFormLabel-root, & .MuiTableCell-root .MuiTypography-root':
          {
            color: `${theme.palette.error.main} !important`,
          },
    }
    : undefined

const useStyles = makeStyles((theme) => ({
  attachmentBox: {
      cursor: 'pointer',
      display: 'flex',
      borderRadius: '6px',
      backgroundColor: '#172e34',
      padding: theme.spacing(1),
      margin: theme.spacing(1, 0),
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative', 
      height: '100px', 
      overflow: 'hidden'
  },
  attachmentIcon: {
      marginRight: theme.spacing(1),
  },
  delete:{
      m:2,
      display:'flex',
      justifyContent:'flex-end' 
    },
  checkbox: {
      position: 'absolute',
      top: theme.spacing(1), 
      right: theme.spacing(1), 
  },
  checkboxContainer: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
  },
  dialogContent: {
      padding: theme.spacing(3),
      maxWidth: 768,
      margin: 'auto',
      display: 'flex',
      flexDirection:'column',
      justifyContent: 'center',
      alignItems: 'center',
  },
  gridContainer: {
      height: 'auto',
  },
}));

const StyledTabs = withStyles({
  root: {
    minHeight: 45,
  },
  indicator: {
    display: "none", // hide default underline
  },
})(Tabs);

const StyledTab = withStyles((theme) => ({
  root: {
    minHeight: 45,
    flex: 1, // 50% width
    maxWidth: "none",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 6,
    margin: "0 4px",
    transition: "0.2s",

    // &&& beats globalStyles tab transparent/!important overrides (this component only)
    "&&&.Mui-selected": {
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: `${theme.palette.primary.contrastText} !important`,
    },
    "&&&.Mui-selected .MuiTab-wrapper, &&&.Mui-selected .MuiSvgIcon-root, &&&.Mui-selected svg": {
      color: `${theme.palette.primary.contrastText} !important`,
      fill: `${theme.palette.primary.contrastText} !important`,
    },

    "&:not(.Mui-selected)": {
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.primary.main}`, // ✅ FIXED
    },
  },
}))(Tab);

const getStatusColorAndLabel = (status, hold, design_order_job_status, send_client_app_req, designs, is_from_client, is_in_queue) => {
  // HOLD: hold=1 AND status !=0
  if (hold == 1 && status != 0) {
    return {
      color: 'warning',
      label: 'Status: Hold',
    }
  }
  
  // REJECTED: status=6 AND hold=0
  if (status == 6 && hold == 0) {
    return {
      color: 'error',
      label: 'Status: Rejected',
    }
  }
  
  // APPROVED: status=2 AND hold=0
  if (status == 2 && hold == 0) {
    return {
      color: 'success',
      label: 'Status: Approved',
    }
  }
  
  // DESIGN JOB COMPLETED: design_order_job_status = 3 AND send_client_app_req=0 AND status !=0 AND hold = 0
  if (design_order_job_status == 3 && send_client_app_req == 0 && status != 0 && hold == 0) {
    return {
      color: 'success',
      label: 'Status: Design Job Completed',
    }
  }
  
  // Check if there are design attachments (doa.id IS NOT NULL equivalent)
  // Check for any attachment with isDesign == 1
  const hasDesignAttachments = designs && Array.isArray(designs) && designs.length > 0 ;
  
  // For status 1, check conditions in priority order
  if (status == 1 && hold == 0) {
    // Handle null/undefined for design_order_job_status
    const jobStatus = design_order_job_status != null ? design_order_job_status : 0;
    
    // AWAITING APPROVAL: status=1 AND hold=0 AND send_client_app_req=1 AND doa.id IS NOT NULL
    if (send_client_app_req == 1 && hasDesignAttachments) {
      return {
        color: 'warning',
        label: 'Status: Awaiting Approval',
      }
    }
    
    // IN DESIGN: doa.id IS NOT NULL AND status = 1 AND hold = 0 AND send_client_app_req=0 AND design_order_job_status != 3
    // This must be checked before NEW to ensure it takes priority when attachments exist
    // if (send_client_app_req == 0 && jobStatus != 3 && hasDesignAttachments) {
    if (send_client_app_req == 0 && jobStatus == 2) {
      return {
        color: 'info',
        label: 'Status: In Design',
      }
    }
    
    // NEW: status=1 AND hold=0 AND doa.id IS NULL AND design_order_job_status != 3 AND send_client_app_req=0
    // Only show NEW if there are NO design attachments
    if (send_client_app_req == 0 && jobStatus == 1 && is_in_queue == 1) {
      return {
        color: 'info',
        label: 'Status: In Queue',
      }
    }
    if (send_client_app_req == 0 && jobStatus == 1 && is_in_queue == 0) {
      return {
        color: 'info',
        label: 'Status: New',
      }
    }
    
    // Default fallback for status 1
    return {
      color: 'info',
      label: 'Status: Open',
    }
  }
  
  // Other status cases
  switch (status) {
    case 3:
      return {
        color: hold == 1 ? 'warning' : 'success',
        label: hold == 1 ? 'Status: Hold' : 'Status: Approved',
      }
    case 4:
      return {
        color: hold == 1 ? 'warning' : 'warning',
        label: hold == 1 ? 'Status: Hold' : 'Status: Draft',
      }
    case 5:
      return {
        color: hold == 1 ? 'warning' : 'warning',
        label: hold == 1 ? 'Status: Hold' : 'Status: Closed',
      }
    case 6:
      return {
        color: hold == 1 ? 'warning' : 'error',
        label: hold == 1 ? 'Status: Hold' : 'Status: Rejected',
      }
    default:
      return {
        color: hold == 1 ? 'warning' : 'default',
        label: hold == 1 ? 'Status: Hold' : 'Status: Open',
      }
  }
}

export default function ViewDesignOrderDetails({
  designs,
  referenceFiles,
  DesignOrderProject,
  columnView,
  products,
  user,
  setDesigns,
  details,
  fetchRequestDetails,
  isRequest,
  requestId,
  signAgreements,
  customTemplate,
  questionnaireTemplate,
  allDataEmpty,
  portfolio,
  project,
  clientDetails,
  teamMembers,
  invoice,
  rejectedAttachments,
  quotation,
  proposalView,
  isDesign,
  isFromTask,
  setRefresh,
  refresh,
  allTeamMembers,
  designStatus,
  onlineUsers=[],
  collaborators=[],
  collaboratorsTeamMembers=[],
  designOrderProducts=[],
  designOrderServices=[],
  customerOrderProductGroups = [],
}) {
  console.log('allTeamMembersallTeamMembers=',allTeamMembers)
console.log('detailsdetails=',details)
console.log('isDesignisDesign=',isDesign)
const rejectType=[
  {id:1, value:'CLIENT REVISION'},
   {id:2, value:'DESIGN REVISION'},
   {id:3, value:'SALES REVISION'},
  ]
console.log('columnViewcolumnView=',columnView)
  const fileInputRef = useRef(null)
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const {
    name,
    notes,
    description,
    services,
    start_date,
    due_date,
    status,
    reason,
    reject_type,
    requested_due_date,
    reject_file,
    reject_file_type,
    hold_reason,
    hold,
    reasonWithQb,
    estimate_id,
    added_by,
    awarded,
    rfpQbId,
    community_name,
    id,
    send_client_app_req,
    sales_rep_names
  } = details
  console.log('detailsdetailsdetailsdetails',details)
  console.log('rejectedAttachmentsrejectedAttachments=',rejectedAttachments)
  const submissionDate = formatDateOnly(start_date)
  const endDate = formatDateOnly(due_date)
  const xs = useMediaQuery(theme.breakpoints.down('xs'))
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  const mdd = useMediaQuery(theme.breakpoints.down('md'))
  const xld = useMediaQuery(theme.breakpoints.up('xl'))
  const [progress, setProgress] = useState(false)
  const [showAllCollaborators, setShowAllCollaborators] = useState(false)
  const [resolvedProductGroups, setResolvedProductGroups] = useState(
    () => customerOrderProductGroups || [],
  )
  const resolvedProductGroupsMap = useMemo(
    () =>
      new Map(
        (resolvedProductGroups || []).map((g) => [String(g?.id ?? g?.group_id), g]),
      ),
    [resolvedProductGroups],
  )
  const [modalClose, setModalClose] = useState(false)
  const [openProductModel, setOpenProductModel] = useState(false)
  const [openReasonModal, setOpenReasonModal] = useState(false)
  const [itemId, setItemId] = useState(null)
  const [reasonText, setReasonText] = useState('')
  const [openImageModal,setOpenImageModal] = useState(false)
  const [openImageModalReference,setOpenImageModalReference] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(null);
  const classes = useStyles();
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [selectedPDFItem, setSelectedPDFItem] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [useOfficeViewer, setUseOfficeViewer] = useState(true);
  const [activeTab, setActiveTab] = useState('designs');
  const [activeTabNew, setActiveTabNew] = useState('designs');
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [downloadSelectMode, setDownloadSelectMode] = useState(false);
  const [selectedReferences, setSelectedReferences] = useState([]);
  const [referenceDownloadSelectMode, setReferenceDownloadSelectMode] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [emailModalDesigns, setEmailModalDesigns] = useState([]);
  const [pagePdf, setPagePdf] = useState(0);
  const [rowsPerPagePdf, setRowsPerPagePdf] = useState(3);

  const [pageImg, setPageImg] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const [showRejectedAttachments, setShowRejectedAttachments] = useState(false);
  const [openPDFEditor, setOpenPDFEditor] = useState(false);
  const [selectedPDFForEdit, setSelectedPDFForEdit] = useState(null);
  const [selectedCatalogProducts, setSelectedCatalogProducts] = useState([]);
  const [isCatalogUnlinkMode, setIsCatalogUnlinkMode] = useState(false);

  const handleChangePagePdf = (event, newPage) => {
    setPagePdf(newPage);
  };

  const handleChangeRowsPerPagePdf = (event) => {
    setRowsPerPagePdf(parseInt(event.target.value, 10));
    setPagePdf(0);
  };

  const handleChangePageImg = (event, newPage) => {
    setPageImg(newPage);
  };

  const handleChangeRowsPerPageImg = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageImg(0);
  };
  
  const approvedDesigns = designs?.filter(item=>item.approved == 1)
  const isClient = user?.isClient;
  let userType = isClient ? 3 : user?.user_type;
  let userId = user.user_id

  if(user.added_by_client && user.parent_user_type && user.parent_id) {
    userId = user.parent_id
    userType =3 //user.parent_user_type
  }

  const [productAttachments, setProductAttachments] = useState({})
  const [openProductAttachment, setOpenProductAttachment] = useState({})
  const [attachmentTabValue, setAttachmentTabValue] = useState({})
  const [selectedUploadedImages, setSelectedUploadedImages] = useState({})
  const [isUploadingProduct, setIsUploadingProduct] = useState({})
  const [productAttachmentDragOver, setProductAttachmentDragOver] = useState({})

  const getProductAttachmentKey = (item) => item?.id || item?.product_index

  const findDesignOrderProductForAttachmentFile = useCallback(
    (file) =>
      (designOrderProducts || []).find((product) => {
        const key = getProductAttachmentKey(product)
        if (!key) return false
        const list = productAttachments[key] || productAttachments[String(key)] || []
        return list.some((attachment) => attachmentMatches(attachment, file))
      }),
    [designOrderProducts, productAttachments],
  )

  useEffect(() => {
    const next = {}
    ;(designOrderProducts || []).forEach((item) => {
      const key = getProductAttachmentKey(item)
      if (key) {
        next[key] = Array.isArray(item?.attachments)
          ? item.attachments.map((file) =>
              mapDesignOrderDetailsProductAttachmentForDisplay(
                file,
                item,
                resolvedProductGroupsMap,
              ),
            )
          : []
      }
    })
    setProductAttachments(next)
  }, [designOrderProducts, resolvedProductGroupsMap])

  const handleAddAttachment = (item) => {
    const key = getProductAttachmentKey(item)
    if (!key) return
    setOpenProductAttachment((prev) => ({ ...prev, [key]: true }))
    setAttachmentTabValue((prev) => ({ ...prev, [key]: 0 }))
    const currentAttachments = productAttachments[key] || productAttachments[String(key)] || []
    setSelectedUploadedImages((prev) => ({ ...prev, [String(key)]: [...currentAttachments] }))
  }

  const handleCloseAttachment = (item) => {
    const key = getProductAttachmentKey(item)
    if (!key) return
    setOpenProductAttachment((prev) => ({ ...prev, [key]: false }))
    setAttachmentTabValue((prev) => ({ ...prev, [key]: 0 }))
    setSelectedUploadedImages((prev) => ({ ...prev, [key]: [] }))
  }

  const handleAttachmentTabChange = (event, newValue, productKey) => {
    setAttachmentTabValue((prev) => ({ ...prev, [productKey]: newValue }))
  }

  const handleImageSelect = (file, productKey, isSelected) => {
    const key = String(productKey ?? '')
    setSelectedUploadedImages((prev) => {
      const current = prev[key] || prev[productKey] || []
      if (isSelected) {
        if (current.some((f) => attachmentMatches(f, file))) return prev
        return { ...prev, [key]: [...current, file] }
      }
      return { ...prev, [key]: current.filter((f) => !attachmentMatches(f, file)) }
    })
  }

  const getProductAttachmentsList = useCallback(
    (productKey) =>
      productAttachments[productKey] || productAttachments[String(productKey)] || [],
    [productAttachments],
  )

  const getGroupMemberAttachments = useCallback(
    (members) => {
      const seen = new Set()
      const merged = []
      ;(members || []).forEach((member) => {
        const key = getProductAttachmentKey(member)
        const list = productAttachments[key] || productAttachments[String(key)] || []
        list.forEach((file) => {
          const fileKey = attachmentFileKey(file)
          if (fileKey && seen.has(fileKey)) return
          if (fileKey) seen.add(fileKey)
          merged.push(file)
        })
      })
      return merged
    },
    [productAttachments],
  )

  const canEditProductAttachments = (productItem) =>
    !user.isVendor &&
    !isClient &&
    !project &&
    ((designStatus !== 2 && send_client_app_req === 0) ||
      designStatus === 6 ||
      productItem?.isDesign === 2)

  const uploadedDesignFiles = useMemo(
    () =>
      (designs || [])
        .filter((design) => design.is_reference !== 1 && !design.design_order_product_id)
        .map((design) => ({
          url: design.name || design.url || design.file_url,
          name: design.name || design.url || design.file_url,
          file_type: design.file_type,
          extension: design.extension,
          size: design.file_size || design.size,
          id: design.id,
        })),
    [designs],
  )

  const handleProductDrop = async (acceptedFiles, item) => {
    const key = getProductAttachmentKey(item)
    if (!key || !acceptedFiles || acceptedFiles.length === 0) return

    setIsUploadingProduct((prev) => ({ ...prev, [key]: true }))
    try {
      const uploadPromises = acceptedFiles.map(async (file) => {
        const timestamp = Date.now()
        const sanitizedFileName = file.name.replace(/\s+/g, '_')
        const fileName = `${timestamp}_${sanitizedFileName}`
        const filePath = `design-order/product-attachments/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from('signwise-attachments')
          .upload(filePath, file)

        if (uploadError) throw uploadError

        const { data: publicUrlData, error: urlError } = supabase.storage
          .from('signwise-attachments')
          .getPublicUrl(filePath)

        if (urlError || !publicUrlData?.publicUrl) {
          throw new Error('Failed to generate public URL')
        }

        return mapDesignOrderDetailsProductAttachmentForDisplay({
          url: publicUrlData.publicUrl,
          file_type: file.type,
          extension: file.name.split('.').pop(),
          size: file.size,
          name: file.name,
        }, item, resolvedProductGroupsMap)
      })

      const uploadedFiles = await Promise.all(uploadPromises)
      const res = await axios.post(`${REST_API_END_POINT}request/add-design-order-product-attachments`, {
        userId,
        userType,
        design_order_id: details?.id,
        design_order_product_id: item?.id,
        attachments: uploadedFiles,
      })

      if (res.data.status === 1) {
        const savedFiles = (res.data.data || uploadedFiles).map((file) =>
          mapDesignOrderDetailsProductAttachmentForDisplay(file, item, resolvedProductGroupsMap),
        )
        setProductAttachments((prev) => {
          const current = prev[key] || []
          const existingUrls = current.map((file) => file.url || file.name)
          const newFiles = savedFiles.filter((file) => !existingUrls.includes(file.url || file.name))
          return { ...prev, [key]: [...current, ...newFiles] }
        })
        enqueueSnackbar(res.data.message || 'Product attachments uploaded successfully', { variant: 'success' })
        fetchRequestDetails()
      } else {
        enqueueSnackbar(res.data.message || 'Failed to upload product attachments', { variant: 'error' })
      }
    } catch (error) {
      console.error('Error uploading product attachment:', error)
      enqueueSnackbar('Failed to upload product attachments', { variant: 'error' })
    } finally {
      setIsUploadingProduct((prev) => ({ ...prev, [key]: false }))
    }
  }

  const handleAddSelectedProductAttachments = async (item) => {
    const key = getProductAttachmentKey(item)
    if (!key || !item?.id) return

    const selected = selectedUploadedImages[key] || selectedUploadedImages[String(key)] || []
    const original = getProductAttachmentsList(key)

    // Newly selected to be linked: present in selected, but not in original
    const toLink = selected.filter((file) => !original.some((orig) => attachmentMatches(orig, file)))

    // Unchecked to be unlinked: present in original, but not in selected
    const toUnlink = original.filter((file) => !selected.some((sel) => attachmentMatches(sel, file)))

    if (toLink.length === 0 && toUnlink.length === 0) {
      setOpenProductAttachment((prev) => ({ ...prev, [key]: false }))
      return
    }

    setIsUploadingProduct((prev) => ({ ...prev, [key]: true }))
    try {
      const linkPromises = toLink.map((file) =>
        axios.post(`${REST_API_END_POINT}design/link-design-to-catalog`, {
          id: file.id,
          design_order_id: details?.id,
          design_order_product_id: item.id,
          userId: user?.user_id || userId,
        })
      )

      const unlinkPromises = toUnlink.map((file) =>
        axios.post(`${REST_API_END_POINT}design/unlink-design-from-catalog`, {
          id: file.id,
          design_order_id: details?.id,
          userId: user?.user_id || userId,
        })
      )

      await Promise.all([...linkPromises, ...unlinkPromises])

      enqueueSnackbar('Product attachments updated successfully', { variant: 'success' })
      setSelectedUploadedImages((prev) => ({ ...prev, [key]: [], [String(key)]: [] }))
      setOpenProductAttachment((prev) => ({ ...prev, [key]: false }))
      fetchRequestDetails()
    } catch (error) {
      console.error('Error updating product attachments:', error)
      enqueueSnackbar('Failed to update product attachments', { variant: 'error' })
    } finally {
      setIsUploadingProduct((prev) => ({ ...prev, [key]: false }))
    }
  }

  const renderProductAttachmentModalContent = (item) => {
    const productKey = getProductAttachmentKey(item)

    return (
      <Grid container spacing={3} sx={{ justifyContent: 'center', width: '100%' }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Upload Attachment
          </Typography>
          <Tabs
            value={attachmentTabValue[productKey] || 0}
            onChange={(e, newValue) => handleAttachmentTabChange(e, newValue, productKey)}
            sx={{ mb: 2 }}
          >
            <Tab label="Select from Uploaded" />
            <Tab label="Upload New" />
          </Tabs>

          {(attachmentTabValue[productKey] || 0) === 0 ? (
            <Box>
              {(() => {
                const allProductAttachmentFiles = Object.values(productAttachments).flat()

                const availableDesignFiles = uploadedDesignFiles.filter((file) => {
                  return !allProductAttachmentFiles.some((pfile) => attachmentMatches(pfile, file))
                })

                const allFiles = [
                  ...availableDesignFiles,
                  ...allProductAttachmentFiles.map((file) => {
                    const ownerProduct = findDesignOrderProductForAttachmentFile(file)
                    if (ownerProduct) {
                      return mapDesignOrderDetailsProductAttachmentForDisplay(
                        file,
                        ownerProduct,
                        resolvedProductGroupsMap,
                      )
                    }
                    return file
                  }),
                ]

                const allFilesUnique = (() => {
                  const seen = []
                  return allFiles.filter((file) => {
                    if (seen.some((f) => attachmentMatches(f, file))) return false
                    seen.push(file)
                    return true
                  })
                })()

                if (allFilesUnique.length === 0) {
                  return (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 4 }}
                    >
                      No uploaded files available. Please upload files first.
                    </Typography>
                  )
                }

                const availableImages = allFilesUnique.filter((file) => {
                  const isAddedToOtherProduct = Object.entries(productAttachments).some(
                    ([attachmentKey, attachments]) => {
                      if (String(attachmentKey) === String(productKey)) return false
                      return (
                        attachments &&
                        attachments.some((f) => attachmentMatches(f, file))
                      )
                    },
                  )
                  return !isAddedToOtherProduct
                })

                if (availableImages.length === 0) {
                  return (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 4 }}
                    >
                      All images are added to other products. No images left in the uploaded files.
                    </Typography>
                  )
                }

                const selectionKey = String(productKey)
                const rowSelection =
                  selectedUploadedImages[selectionKey] ||
                  selectedUploadedImages[productKey] ||
                  []

                const original = getProductAttachmentsList(productKey)
                const toLink = rowSelection.filter((file) => !original.some((orig) => attachmentMatches(orig, file)))
                const toUnlink = original.filter((file) => !rowSelection.some((sel) => attachmentMatches(sel, file)))
                const hasChanges = toLink.length > 0 || toUnlink.length > 0

                return (
                  <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {availableImages.map((file, idx) => {
                        const isSelected = rowSelection.some((f) => attachmentMatches(f, file))
                        const isAlreadyAddedToCurrent = original.some(
                          (f) => attachmentMatches(f, file),
                        )
                        const url = file.url || file.name
                        const preview = file.preview || url
                        const fileExt = (
                          file.extension ||
                          getFileExtension(url) ||
                          ''
                        ).toLowerCase()
                        const isOffice =
                          isOfficeAttachment(url, file.file_type) ||
                          ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv'].includes(fileExt)
                        const isDesign = ['ai', 'psd', 'eps', 'dwg', 'dxf'].includes(fileExt)
                        const isPdf = file.file_type === 'application/pdf' || fileExt === 'pdf'

                        return (
                          <Grid item xs={6} sm={4} md={3} key={`${url}-${idx}`}>
                            <Card
                              sx={{
                                position: 'relative',
                                border:
                                  isSelected ? '2px solid' : '1px solid',
                                borderColor:
                                  isSelected
                                    ? 'primary.main'
                                    : 'divider',
                                cursor: 'pointer',
                                opacity: 1,
                              }}
                              onClick={() => {
                                handleImageSelect(file, productKey, !isSelected)
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  paddingTop: '100%',
                                  overflow: 'hidden',
                                }}
                              >
                                {isPdf || isOffice || isDesign ? (
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setPdfLoading(true)
                                      setSelectedPDF(url)
                                      setSelectedPDFItem(file)
                                      setOpenPDFModal(true)
                                    }}
                                    aria-label="open file"
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      p: 0,
                                      borderRadius: 0,
                                    }}
                                  >
                                    <Box
                                      component="iframe"
                                      src={
                                        isPdf
                                          ? url
                                          : isOffice
                                          ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
                                          : `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`
                                      }
                                      title="File Preview"
                                      frameBorder="0"
                                      // sandbox="allow-scripts allow-same-origin" before it was this caused pdf preview not working and fixed with below code, if any future issues came i have done only the pdf part that will only in me others i will be author
                                      sandbox={isPdf ? undefined : 'allow-scripts allow-same-origin'}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        pointerEvents: 'none',
                                      }}
                                    />
                                  </IconButton>
                                ) : (
                                  <Box
                                    component="img"
                                    src={preview}
                                    alt={file.name || 'Uploaded file'}
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                    onError={(e) => {
                                      if (preview !== url) {
                                        e.target.src = url
                                      }
                                    }}
                                  />
                                )}
                                <AttachmentDownloadIconButton
                                  file={file}
                                  fileIndex={idx}
                                  hidden={isClient || userType === 3}
                                  sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2 }}
                                />
                                <Checkbox
                                  checked={isSelected}
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    zIndex: 3,
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation()
                                    handleImageSelect(file, productKey, e.target.checked)
                                  }}
                                />
                                {isAlreadyAddedToCurrent && isSelected && (
                                  <Chip
                                    label="Added"
                                    size="small"
                                    color="success"
                                    sx={{ position: 'absolute', bottom: 4, left: 4 }}
                                  />
                                )}
                              </Box>
                            </Card>
                          </Grid>
                        )
                      })}
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <AttachmentDownloadAllButton
                        files={rowSelection}
                        allFiles={availableImages}
                        onToggleSelectAll={(checked) => {
                          const key = String(productKey)
                          setSelectedUploadedImages((prev) => ({
                            ...prev,
                            [key]: checked ? [...availableImages] : [],
                            [productKey]: checked ? [...availableImages] : [],
                          }))
                        }}
                        label="Download Selected"
                        hidden={isClient || userType === 3}
                      />
                      <Button
                        variant="contained"
                        onClick={() => handleAddSelectedProductAttachments(item)}
                        disabled={!hasChanges || isUploadingProduct[productKey]}
                      >
                        Save Selection ({rowSelection.length})
                      </Button>
                    </Box>
                  </>
                )
              })()}
            </Box>
          ) : (
            <UploadMultiFile
              showPreview
              multiple
              maxSize={1024 * 1024 * 1024}
              files={getProductAttachmentsList(productKey).map((file) =>
                mapDesignOrderDetailsProductAttachmentForDisplay(
                  file,
                  item,
                  resolvedProductGroupsMap,
                ),
              )}
              onDrop={(acceptedFiles) => handleProductDrop(acceptedFiles, item)}
              onRemove={() => {}}
              onRemoveAll={() => {}}
              isUploading={isUploadingProduct[productKey] || false}
              showProductLabels
            />
          )}
        </Grid>
      </Grid>
    )
  }

  const getDragTransferTypes = (dataTransfer) => {
    if (!dataTransfer?.types) return []
    const { types } = dataTransfer
    if (typeof types.includes === 'function') return [...types]
    const list = []
    for (let i = 0; i < types.length; i += 1) {
      list.push(types[i])
    }
    return list
  }

  const isExternalFileDragEvent = (e) => {
    const types = getDragTransferTypes(e.dataTransfer)
    if (types.some((type) => String(type).toLowerCase().includes('react-beautiful-dnd'))) {
      return false
    }
    return (
      types.includes('Files') ||
      types.includes('application/x-moz-file') ||
      Array.from(e.dataTransfer?.items || []).some((item) => item.kind === 'file')
    )
  }

  const handleProductIconDragOver = (e, item) => {
    const key = getProductAttachmentKey(item)
    if (!key || !isExternalFileDragEvent(e)) return
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy'
    }
    setProductAttachmentDragOver((prev) => ({ ...prev, [key]: true }))
  }

  const handleProductIconDragLeave = (e, item) => {
    const key = getProductAttachmentKey(item)
    if (!key) return
    e.preventDefault()
    e.stopPropagation()
    const related = e.relatedTarget
    if (related && e.currentTarget.contains(related)) return
    setProductAttachmentDragOver((prev) => ({ ...prev, [key]: false }))
  }

  const handleProductIconDrop = (e, item) => {
    const key = getProductAttachmentKey(item)
    if (!key) return
    e.preventDefault()
    e.stopPropagation()
    setProductAttachmentDragOver((prev) => ({ ...prev, [key]: false }))
    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length > 0) {
      handleProductDrop(files, item)
    }
  }

  useEffect(() => {
    setResolvedProductGroups(customerOrderProductGroups || [])
  }, [customerOrderProductGroups])

  useEffect(() => {
    if (customerOrderProductGroups?.length) return
    const designOrderId = details?.id || requestId
    const hasGrouped = (designOrderProducts || []).some((p) =>
      getDesignOrderProductGroupId(p),
    )
    if (!designOrderId || !hasGrouped || !user?.user_id) return

    let active = true
    const fetchGroups = async () => {
      try {
        const isClient = Boolean(user?.isClient)
        const userType = isClient ? 3 : user?.user_type
        let userId = user?.user_id
        if (user?.added_by_client && user?.parent_id) {
          userId = user.parent_id
        }
        const res = await axios.post(`${REST_API_END_POINT}design/get-design-order-by-id`, {
          design_order_id: designOrderId,
          userId,
          userType,
          isClient,
        })
        if (!active) return
        setResolvedProductGroups(res.data?.customerOrderProductGroups || [])
      } catch {
        if (!active) return
        setResolvedProductGroups([])
      }
    }
    fetchGroups()
    return () => {
      active = false
    }
  }, [customerOrderProductGroups, designOrderProducts, details?.id, requestId, user])

  const designOrderServiceRows = Array.isArray(designOrderServices)
    ? designOrderServices
    : Array.isArray(details?.servicesData)
      ? details.servicesData
      : []

  const designOrderMarkupType = Number(details?.markup_type ?? invoice?.markup_type) || 2

  const groupedDesignOrderDisplayRows = useMemo(
    () =>
      buildGroupedDesignOrderDisplayRows({
        designOrderProducts,
        customerOrderProductGroups: resolvedProductGroups,
        markupType: designOrderMarkupType,
      }),
    [designOrderProducts, resolvedProductGroups, designOrderMarkupType],
  )

  const getDesignOrderNonGroupProductSubtotal = useCallback((item) => {
    if (!designOrderProductShowsPricing(item)) return 0
    return getDesignOrderProductSubtotal(item)
  }, [])

  const productTotal = useMemo(
    () =>
      (groupedDesignOrderDisplayRows || []).reduce((sum, row) => {
        if (row?.__type === 'group') {
          const groupHasInstall = Boolean(row?.groupHasInstall)
          return (
            sum +
            (groupHasInstall
              ? Number(row?.groupProductOnlyTotal ?? 0)
              : Number(row?.totalValue ?? 0))
          )
        }
        return sum + getDesignOrderNonGroupProductSubtotal(row)
      }, 0),
    [groupedDesignOrderDisplayRows, getDesignOrderNonGroupProductSubtotal],
  )

  const productInstallTotal = useMemo(
    () =>
      (groupedDesignOrderDisplayRows || []).reduce((sum, row) => {
        if (row?.__type === 'group') {
          return sum + Number(row?.groupInstallSubtotal ?? 0)
        }
        return sum + getDesignOrderProductInstallTotal(row)
      }, 0),
    [groupedDesignOrderDisplayRows],
  )
  const storedServiceTotal = Number(details?.total_cost ?? invoice?.total_cost ?? 0) || 0
  const installServiceTotal = designOrderServiceRows.reduce(
    (sum, service) =>
      Number(service?.labour_cost_service_id) === 10
        ? sum + getDesignOrderServiceTotal(service)
        : sum,
    0,
  )
  const additionalServicesTotal = designOrderServiceRows.length
    ? designOrderServiceRows.reduce(
      (sum, service) =>
        Number(service?.labour_cost_service_id) === 10
          ? sum
          : sum + getDesignOrderServiceTotal(service),
      0,
    )
    // : storedServiceTotal
    : 0
  const installTotal = productInstallTotal + installServiceTotal
  const designOrderSubtotal = productTotal + installTotal + additionalServicesTotal
  const designOrderDiscount = Number(invoice?.extra_discount ?? details?.extra_discount ?? 0) || 0
  const designOrderTax = Number(invoice?.tax_amount ?? details?.tax_amount ?? details?.tax ?? 0) || 0
  const designOrderTotal = designOrderSubtotal - designOrderDiscount + designOrderTax
  const showDesignOrderSummary = designOrderSubtotal > 0 || designOrderDiscount > 0 || designOrderTax > 0

  console.log("detailsdetails",invoice);
  const handleUpdateDesign = (data) => { 
    setModalClose(true)
    data.requestId = details.id
    data.userId = user.user_id
    if (data.approved) data.approved_by = user.user_id
    if(data.approved === 0 && reasonText === ''){
      enqueueSnackbar('Please add Reason', { variant: 'error' })
      return
    }
    let productName = ''
    if (data?.hasOwnProperty('product_name')) {
      productName = data?.product_name
      delete data?.product_name;
    }

    axios
      .post(`${REST_API_END_POINT}request/update-designs`, data)
      .then((res) => {
        if (res.data.status === 1) {
          enqueueSnackbar(data.approved === 1 ? 'Design has been Approved' : data.product_id ? `Design has been linked with ${productName}` : data?.status==0 ? 'Design has been Deleted' : 'Design has been Rejected', { variant: 'success' })
          fetchRequestDetails()
        } else {
          enqueueSnackbar(res.data.message, { variant: 'error' })
        }
        setItemId(null)
        setModalClose(false)
        setOpenProductModel(false)
        setOpenReasonModal(false)
        setReasonText('')
      })
  }
  const refreshAfterDesignChange = () => {
    if (typeof setRefresh === 'function') {
      setRefresh(!refresh)
    } else if (typeof fetchRequestDetails === 'function') {
      fetchRequestDetails()
    }
  }

  const handleDeleteDesign = (data) => { 
    axios
      .post(`${REST_API_END_POINT}design/delete-design`, data)
      .then((res) => {
        if (res.data.status === 1) {
          enqueueSnackbar('Design has been Deleted' , { variant: 'success' })
          refreshAfterDesignChange()
        } else {
          enqueueSnackbar(res.data.message, { variant: 'error' })
        }
      })
  }

  const linkableCatalogProducts = useMemo(() => {
    const source = sortDesignOrderProductsByIndex(
      (designOrderProducts || []).filter((product) => product?.id),
    )
    if (source.length > 0) {
      const seenGroups = new Set()
      return source.filter((product) => {
        const groupId = getDesignOrderProductGroupId(product)
        if (!groupId) return true
        const groupKey = String(groupId)
        if (seenGroups.has(groupKey)) return false
        seenGroups.add(groupKey)
        return true
      })
    }
    return (products || []).filter(
      (product) => product?.material === 0 && product?.rfp === 0,
    )
  }, [designOrderProducts, products])

  const getCatalogProductLabel = (product) =>
    getDesignOrderDetailsCatalogLinkLabel(product, resolvedProductGroupsMap)

  const getCatalogProductKey = (product) => product?.id || product?.product_id

  const getLinkedCatalogProductForDesign = (designItem) => {
    if (!designItem?.design_order_product_id) return null
    const linkedId = Number(designItem.design_order_product_id)
    return (
      linkableCatalogProducts.find(
        (product) => Number(getCatalogProductKey(product)) === linkedId,
      ) ||
      sortDesignOrderProductsByIndex(
        (designOrderProducts || []).filter((product) => product?.id),
      ).find((product) => Number(getCatalogProductKey(product)) === linkedId)
    )
  }

  const isCatalogProductSelected = (product) =>
    selectedCatalogProducts.some(
      (selected) => getCatalogProductKey(selected) === getCatalogProductKey(product),
    )

  const initializeSelectedCatalogProducts = (designItem, forUnlink = false) => {
    if (!forUnlink || !designItem?.design_order_product_id) {
      setSelectedCatalogProducts([])
      return
    }
    const linkedProduct =
      linkableCatalogProducts.find(
        (product) => Number(getCatalogProductKey(product)) === Number(designItem.design_order_product_id),
      ) ||
      sortDesignOrderProductsByIndex(
        (designOrderProducts || []).filter((product) => product?.id),
      ).find(
        (product) => Number(getCatalogProductKey(product)) === Number(designItem.design_order_product_id),
      )
    setSelectedCatalogProducts(linkedProduct ? [linkedProduct] : [])
  }

  const handleCatalogCheckboxChange = (product, isChecked) => {
    setSelectedCatalogProducts((prevSelected) => {
      if (isChecked) {
        return [...prevSelected, product]
      }
      return prevSelected.filter(
        (selected) => getCatalogProductKey(selected) !== getCatalogProductKey(product),
      )
    })
  }

  const closeCatalogProductModal = () => {
    setModalClose(false)
    setOpenProductModel(false)
    setSelectedCatalogProducts([])
    setIsCatalogUnlinkMode(false)
  }

  const handleSubmitCatalogLink = (designId) => {
    if (selectedCatalogProducts.length === 0) {
      enqueueSnackbar('Please select at least one product', { variant: 'error' })
      return
    }
    if (selectedCatalogProducts.length > 1) {
      enqueueSnackbar('Please select only one product to link', { variant: 'error' })
      return
    }
    handleLinkDesignToCatalog(designId, selectedCatalogProducts[0])
  }

  const handleSubmitCatalogUnlink = (designId) => {
    if (selectedCatalogProducts.length === 0) {
      enqueueSnackbar('Please select a product to unlink', { variant: 'error' })
      return
    }
    handleUnlinkDesignFromCatalog(designId)
  }

  const getCatalogProductsForModal = (designItem) => {
    if (isCatalogUnlinkMode) {
      const linkedId = Number(designItem?.design_order_product_id)
      const fromLinkable = linkableCatalogProducts.filter(
        (product) => Number(getCatalogProductKey(product)) === linkedId,
      )
      if (fromLinkable.length > 0) return fromLinkable
      return sortDesignOrderProductsByIndex(
        (designOrderProducts || []).filter((product) => product?.id),
      ).filter((product) => Number(getCatalogProductKey(product)) === linkedId)
    }
    return linkableCatalogProducts
  }

  const handleLinkDesignToCatalog = (designId, product) => {
    const designOrderProductId = product?.id || product?.product_id
    const productName = product?.name || product?.product_name || 'catalog'

    if (!designOrderProductId) {
      enqueueSnackbar('Invalid catalog item selected', { variant: 'error' })
      return
    }

    setModalClose(true)
    axios
      .post(`${REST_API_END_POINT}design/link-design-to-catalog`, {
        id: designId,
        design_order_id: details?.id,
        design_order_product_id: designOrderProductId,
        userId: user?.user_id,
      })
      .then((res) => {
        if (res.data.status === 1) {
          enqueueSnackbar(`Design has been linked with ${productName}`, { variant: 'success' })
          refreshAfterDesignChange()
        } else {
          enqueueSnackbar(res.data.message, { variant: 'error' })
        }
        setItemId(null)
        setModalClose(false)
        setOpenProductModel(false)
        setSelectedCatalogProducts([])
        setIsCatalogUnlinkMode(false)
      })
      .catch(() => {
        enqueueSnackbar('Failed to link design with catalog', { variant: 'error' })
        setItemId(null)
        setModalClose(false)
        setOpenProductModel(false)
        setSelectedCatalogProducts([])
        setIsCatalogUnlinkMode(false)
      })
  }

  const handleUnlinkDesignFromCatalog = (designId) => {
    setModalClose(true)
    axios
      .post(`${REST_API_END_POINT}design/unlink-design-from-catalog`, {
        id: designId,
        design_order_id: details?.id,
        userId: user?.user_id,
      })
      .then((res) => {
        if (res.data.status === 1) {
          enqueueSnackbar('Design has been unlinked from catalog', { variant: 'success' })
          refreshAfterDesignChange()
        } else {
          enqueueSnackbar(res.data.message, { variant: 'error' })
        }
        setItemId(null)
        setModalClose(false)
        setSelectedCatalogProducts([])
        setIsCatalogUnlinkMode(false)
      })
      .catch(() => {
        enqueueSnackbar('Failed to unlink design from catalog', { variant: 'error' })
        setItemId(null)
        setModalClose(false)
        setSelectedCatalogProducts([])
        setIsCatalogUnlinkMode(false)
      })
  }

  const blockedUploadExtensions = [
    'js', 'jsx', 'ts', 'tsx',
    'html', 'css', 'php', 'py', 'java',
    'c', 'cpp', 'cs', 'sh', 'bat',
    'json', 'xml', 'sql', 'db', 'sqlite',
    'jmx', 'env', 'ini', 'config', 'yml', 'yaml',
  ]

  const getUploadFileType = (fileType, fileName) => {
    const fileExtension = fileName?.split('.').pop()?.toLowerCase()
    const designExtensions = ['eps', 'ai', 'psd', 'tif', 'tiff', 'bmp', 'ps', 'indd', 'indt']
    if (fileType?.startsWith('image/') || designExtensions.includes(fileExtension)) {
      return 'image'
    }
    if (fileType?.startsWith('video/')) {
      return 'video'
    }
    return 'other'
  }

  const handleFileChange = async (event) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    const selectedFiles = Array.from(files)

    const blockedFiles = selectedFiles.filter((file) => {
      const ext = file?.name?.split('.')?.pop()?.toLowerCase()
      return blockedUploadExtensions.includes(ext)
    })

    if (blockedFiles.length > 0) {
      enqueueSnackbar(
        `${blockedFiles.map((f) => f.name).join(', ')} files are not allowed`,
        { variant: 'error' },
      )
      event.target.value = ''
      return
    }

    const maxSizeBytes = 1024 * 1024 * 1024
    const oversizedFiles = selectedFiles.filter((file) => file.size > maxSizeBytes)
    if (oversizedFiles.length > 0) {
      enqueueSnackbar('Please upload files less than 1 GB', { variant: 'error' })
      event.target.value = ''
      return
    }

    try {
      setProgress(true)

      const uploadPromises = selectedFiles.map(async (file, index) => {
        const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase()
        // This removes all #, %, &, + and other dangerous URL characters
const sanitizedName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9.\-_]/g, '');
        const filename = `${Date.now()}_${index}_${sanitizedName}`
        const filePath = `design-order/attachments/${filename}`

        const { error: uploadError } = await supabase.storage
          .from('signwise-attachments')
          .upload(filePath, file)

        if (uploadError) throw uploadError

        const { data: publicUrlData, error: urlError } = supabase.storage
          .from('signwise-attachments')
          .getPublicUrl(filePath)

        if (urlError || !publicUrlData?.publicUrl) {
          throw new Error('Failed to get public URL')
        }

        const downloadURL = publicUrlData.publicUrl
        const fileTypeKey = getUploadFileType(file.type, file.name)

        return {
          fileTypeKey,
          downloadURL,
          fileType: file.type,
          extension: fileExtension,
          size: file.size,
        }
      })

      const validFileUploads = (await Promise.all(uploadPromises)).filter(Boolean)

      if (validFileUploads.length === 0) {
        enqueueSnackbar('No files were uploaded', { variant: 'error' })
        return
      }

      const attachments = validFileUploads.reduce((acc, { downloadURL, fileTypeKey }) => {
        acc[fileTypeKey] = acc[fileTypeKey] || []
        acc[fileTypeKey].push(downloadURL)
        return acc
      }, {})

      const designsPayload = validFileUploads.reduce((acc, file) => {
        const { downloadURL, fileTypeKey, fileType, extension, size } = file
        acc[fileTypeKey] = acc[fileTypeKey] || []
        acc[fileTypeKey].push({
          downloadURL,
          fileType,
          file_type: fileType,
          extension,
          size,
        })
        return acc
      }, {})

      const res = await axios.post(`${REST_API_END_POINT}design-order-job/add-attachment`, {
        attachments,
        designs: designsPayload,
        projectId: details.id,
        design_order_id: details.id,
        userId: user.user_id,
        userType,
        secTab: 'Designs',
      })

      if (res.data.status === 1) {
        enqueueSnackbar(res.data.message || 'Designs uploaded successfully', { variant: 'success' })
        refreshAfterDesignChange()
      } else {
        enqueueSnackbar(res.data.message || 'Failed to upload designs', { variant: 'error' })
      }
    } catch (error) {
      console.error('Error uploading designs:', error)
      enqueueSnackbar(error?.message || 'Failed to upload designs', { variant: 'error' })
    } finally {
      setProgress(false)
      event.target.value = ''
    }
  }
  const approvedCount = designs?.filter(item => item.approved === 1)?.length;
  const rejectedCount = designs?.filter(item => item.approved === 0)?.length;
  
  let borderColor = "GrayText";
  // let borderColor = theme.palette.text.secondary;
  
  // if (rejectedCount > 0) {
  //   borderColor = theme.palette.error.main;
  // } else {
  //   borderColor = theme.palette.success.main;
  // }

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setOpenImageModal(true);
  };
  const openFilePreview = (item) => {
    const fileUrl = item?.url || item?.name
    setPdfLoading(true)
    setSelectedPDF(fileUrl)
    setSelectedPDFItem(item)
    setOpenPDFModal(true)
    setUseOfficeViewer(true)
  }

  const handleImageClickReference = (index) => {
    setCurrentImageIndex(index);
    setOpenImageModalReference(true);
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex < imageDesigns.length - 1 ? prevIndex + 1 : prevIndex));
  };

  const handleSendSelectedDesignsToEmail = () => {
    if (selectedDesigns.length === 0) {
      enqueueSnackbar('Please select at least one design', { variant: 'warning' })
      return
    }
    // Use the base designs array (not filtered by tab) to find selected items from both tabs
    const baseDesigns = details?.send_client_app_req === 1
      ? designs
      : userType == 3 ? approvedDesigns : designs;
    const selectedDesignFiles = baseDesigns?.filter((item) => selectedDesigns.includes(item.id))
    setEmailModalDesigns(selectedDesignFiles)
    setOpenEmailModal(true)
  }

  const handleSendCurrentItemToEmail = (item) => {
    if (!item || !item.id) {
      enqueueSnackbar('No item to send', { variant: 'warning' })
      return
    }
    setEmailModalDesigns([item])
    setOpenEmailModal(true)
  }

  const handleEmailSent = () => {
    setSelectedDesigns([])
  }

  const handleDownloadEditedPdf = async (attachmentId) => {
    try {
      const res = await axios.post(
        `${REST_API_END_POINT}request/get-attachment-images`,
        { attachmentId, documentSource: 'design_order' }
      );
      const images = res.data?.images;
      if (!images || !images.length) {
        enqueueSnackbar('No edited pages available to download', { variant: 'error' });
        return;
      }
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pdfPageWidth = pdf.internal.pageSize.getWidth();
      const pdfPageHeight = pdf.internal.pageSize.getHeight();
      const baseUrl = REST_API.endsWith('/') ? REST_API : `${REST_API}/`;
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const imagePath = (image.image_url || '').startsWith('/') ? image.image_url.slice(1) : image.image_url;
        const imageUrl = (imagePath || '').startsWith('uploads') ? `${baseUrl}${imagePath}` : `${baseUrl}uploads/${imagePath}`;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = imageUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
        });
        const scale = 4;
        const aspectRatio = img.height / img.width;
        const renderWidth = pdfPageWidth;
        const renderHeight = renderWidth * aspectRatio;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = renderWidth * scale;
        canvas.height = renderHeight * scale;
        ctx.scale(scale, scale);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, renderWidth, renderHeight);
        const imgData = canvas.toDataURL('image/png', 1.0);
        if (i !== 0) pdf.addPage();
        const calculatedHeight = pdfPageWidth * aspectRatio;
        const finalHeight = calculatedHeight > pdfPageHeight ? pdfPageHeight : calculatedHeight;
        const finalWidth = finalHeight / aspectRatio;
        pdf.addImage(imgData, 'PNG', 0, 0, finalWidth, finalHeight, undefined, 'FAST');
      }
      pdf.save('edited_document.pdf');
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to download edited PDF', error);
      enqueueSnackbar(error?.message || 'Unable to download edited PDF', { variant: 'error' });
    }
  };
  


  const filteredDesigns = details?.send_client_app_req === 1
  ? designs?.filter((item) =>
      activeTab === 'designs' ? item.isDesign === 1 : item.isDesign !== 1
    )
  : userType == 3 ? approvedDesigns : designs;

  const imageDesigns = filteredDesigns?.filter(
    (item) => item.file_type?.startsWith('image/') || item.file_type === 'img' || item.file_type === 'image'
  );
  
  return (
    <Card>
      <Stack sx={{ m: 3 }} spacing={3}>
        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          sx={{ justifyContent: 'space-between' }}
          spacing={{ xs: 3, sm: 2 }}
        >
          <Typography variant="overline" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            {name}
            {/* {sentenceCase(name)} */}
          </Typography>
          <Stack direction={{ xs: 'row', sm: 'row' }}
          sx={{ justifyContent: 'space-between' }}
          spacing={{ xs: 3, sm: 2 }}>
          {isFromTask ?
           <Typography fontWeight={'0.09rem'} variant="overline">
              <Label
                sx={{ p: '12px' }}
                variant= "filled"
                color={
                  getStatusColorAndLabel(details?.status, details?.hold, details?.design_order_job_status, details?.send_client_app_req, designs, details?.is_from_client, details?.is_in_queue)
                    .color
                }
              >
                {
                  getStatusColorAndLabel(details?.status, details?.hold, details?.design_order_job_status, details?.send_client_app_req, designs, details?.is_from_client, details?.is_in_queue)
                    .label
                }
              </Label>
            </Typography>
          : ''}
        </Stack>
        </Stack>
        {/* {(added_by == 1 && user.user_type!=3) &&
        <Typography variant="subtitle2" sx={{ color: `${(proposalView ? !rfpQbId : (reasonWithQb || !estimate_id)) ? 'red' :'green'}` }}>
          {(proposalView && awarded == null) ? '' : 
          (proposalView ? 
          	(reasonWithQb 
            ? reasonWithQb.split('\n').map((line, index) => (
              <span key={index} style={{ display: 'block' }}>{line}</span>
            )) 
            : 'Not Synced With QuickBooks. A Network Issue has Occurred. Please try again...')
           :
          (estimate_id 
            ? 'Successfully Synced With QuickBooks' 
            : (reasonWithQb 
              ? reasonWithQb.split('\n').map((line, index) => (
                <span key={index} style={{ display: 'block' }}>{line}</span>
              )) 
              : 'Not Synced With QuickBooks. A Network Issue has Occurred. Please try again...')))}
          </Typography>} */}
        <Stack
          direction={{ xs: 'column', sm: 'row',md:'row' }}
          sx={{ justifyContent: 'space-between' }}
          spacing={{ xs: 3, sm: 2 }}
        > 
          <Box sx={{width:'60%'}}>
          <Typography variant="overline" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Design Order Id :</strong> #{id}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Created by :</strong> {clientDetails?.added_by_name || clientDetails?.added_by}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Created On :</strong> {details?.created_at && details.created_at > 0 ? moment.unix(details.created_at).format('MM-DD-YYYY hh:mm:ss A') : 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Client Name :</strong> {clientDetails?.customer_name}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Client Address :</strong> {clientDetails?.customer_address}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Client Phone :</strong> {clientDetails?.customer_phone}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Project Name :</strong> {community_name}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }}>
            <strong>Sales Rep :</strong> {sales_rep_names}
          </Typography>
          </Box>
          <Box sx={{ width: '50%' }}>
            {(details?.status != 2 && (reason || reject_file || rejectedAttachments?.length > 0)) ? 
              <Stack display={'flex'} direction={'column'} gap={1}>
                <Box
                  sx={{
                    backgroundColor: theme.palette.mode === 'light' ? '#e9faff' : '#122227',
                    border: theme.palette.mode === 'light' ? '1px solid #7597a2' : '1px solid #2a3b40',
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                    // width: 'fit-content',
                    maxWidth: 600
                  }}
                >
                  <Stack display={'flex'} direction={'column'} gap={1}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.error.main,
                        fontWeight: 'bold',
                      }}
                    >
                      Reject Reason
                    </Typography>
                    {reason && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.mode === 'light' ? '#000000' : '#ffffff',
                          whiteSpace: 'pre-line',
                          mb: reject_file || rejectedAttachments?.length > 0 ? 2 : 0
                        }}
                      >
                        {reason}
                      </Typography>
                    )}
                    <Stack display={'flex'} direction={'row'} gap={1}>
                       <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.error.main,
                        fontWeight: 'bold',
                      }}
                    >
                      Reject Type :
                    </Typography>
                    {rejectType && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.mode === 'light' ? '#000000' : '#ffffff',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {rejectType.find(item => item.id === reject_type)?.value}
                      </Typography>
                    )}
                    </Stack>
                    <Stack display={'flex'} direction={'row'} gap={1}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.palette.error.main,
                        fontWeight: 'bold',
                      }}
                    >
                      Requested Due Date :
                     </Typography>
                    {requested_due_date && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.mode === 'light' ? '#000000' : '#ffffff',
                          whiteSpace: 'pre-line',
                          mb: reject_file || rejectedAttachments?.length > 0 ? 2 : 0
                        }}
                      >{formatDateOnly(requested_due_date)}</Typography>
                    )}
                    </Stack>
                     {DesignOrderProject && Array.isArray(rejectedAttachments) && rejectedAttachments.length > 0 && (
                      <Button 
                        onClick={() => setShowRejectedAttachments(prev => !prev)}
                        variant="outlined" 
                        size="small" 
                        sx={{ mb: 1, width: 'fit-content' }}
                      >
                        {showRejectedAttachments ? 'Show Rejected Attachments' : 'Hide Rejected Attachments'}
                      </Button>
                    )}
                  </Stack>
                  {showRejectedAttachments || Array.isArray(rejectedAttachments) && rejectedAttachments.length > 0 && (() => {
                    const pdfFiles = rejectedAttachments.filter(f => f.file_type === 'application/pdf');
                    const imageFiles = rejectedAttachments.filter(f => f.file_type !== 'application/pdf');

                    const paginatedPdfFiles = pdfFiles.slice(
                      pagePdf * rowsPerPagePdf,
                      pagePdf * rowsPerPagePdf + rowsPerPagePdf
                    );

                    const paginatedImageFiles = imageFiles.slice(
                      pageImg * rowsPerPage,
                      pageImg * rowsPerPage + rowsPerPage
                    );

                    return (
                      <Box>

                        {/* ---------- PDF FILES ---------- */}
                        {pdfFiles.length > 0 && (
                          <>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 2,
                                mb: 1
                              }}
                            >
                              {paginatedPdfFiles.map((file, index) => (
                                <Box
                                  key={`pdf-${index}`}
                                  sx={{
                                    position: 'relative',
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    height: 120
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => window.open(file.file_url, '_blank')}
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 18,
                                      zIndex: 5,
                                      backgroundColor: '#1ccaff',
                                      color: '#ffffff',
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                                      '&:hover': {
                                        backgroundColor: '#00b4e6'
                                      }
                                    }}
                                  >
                                    <Fullscreen fontSize="small" sx={{ color: '#ffffff' }} />
                                  </IconButton>

                                  <iframe
                                    src={file.file_url}
                                    title={file.file_name || `pdf-${index}`}
                                    style={{
                                      width: '100%',
                                      height: 120,
                                      border: 'none'
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>

                            {pdfFiles.length > 0 && (
                              <TablePagination
                                component="div"
                                count={pdfFiles.length}
                                page={pagePdf}
                                rowsPerPage={rowsPerPagePdf}
                                rowsPerPageOptions={[3, 6, 9]}
                                onPageChange={handleChangePagePdf}
                                onRowsPerPageChange={handleChangeRowsPerPagePdf}
                              />
                            )}
                          </>
                        )}

                        {/* ---------- IMAGE FILES ---------- */}
                        {imageFiles.length > 0 && (
                          <>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 2,
                                mt: 3,
                                mb: 1
                              }}
                            >
                              {paginatedImageFiles.map((file, index) => (
                                <Box
                                  key={`img-${index}`}
                                  component="img"
                                  src={file.file_url}
                                  onClick={() => window.open(file.file_url, '_blank')}
                                  sx={{
                                    width: '100%',
                                    height: 120,
                                    borderRadius: 1,
                                    border: '1px solid #ccc',
                                    cursor: 'pointer',
                                    objectFit: 'cover'
                                  }}
                                />
                              ))}
                            </Box>

                            {imageFiles.length > 0 && (
                              <TablePagination
                                component="div"
                                count={imageFiles.length}
                                page={pageImg}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[3, 6, 9]}
                                onPageChange={handleChangePageImg}
                                onRowsPerPageChange={handleChangeRowsPerPageImg}
                              />
                            )}
                          </>
                        )}

                      </Box>
                    );
                  })()}
                </Box>
                {hold === 1 &&
                  <Typography variant="subtitle2" sx={{ color:theme.palette.error.main,maxWidth:400,overflowWrap: 'break-word',whiteSpace: 'pre-line', }}>
                  {hold_reason ? `Hold Reason: ${hold_reason}` : ""}
                  </Typography>
                }
              </Stack>
            :''}
            {!user.isVendor && (
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }} />
                  <Typography variant="overline" sx={{ color:theme.palette.mode === 'light' ? '#000000' : '#ffffff', fontWeight: 'bold' }}>
                    Requested Start Date:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {submissionDate}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Event sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }} />
                  <Typography variant="overline" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff', fontWeight: 'bold' }}>
                    Due Date:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {endDate}
                  </Typography>
                </Box>
                {requested_due_date && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff' }} />
                    <Typography variant="overline" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff', fontWeight: 'bold' }}>
                      Requested Due Date:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {formatDateOnly(requested_due_date)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2,width: '100%' }}>
                <Box sx={{ flex: 1 ,width: { xs: '100%', sm: '50%' }}}>
                  <Typography alignItems={'center'} variant="subtitle1">
                    Designers List
                  </Typography>
                  <Scrollbar sx={{ width: '100%', height: 100, mt: 1.5 }}>
                    <Stack>
                      {details?.projectMembers?.map((row, index) => (
                        <Stack
                          key={index}
                          direction='row'
                          mb={1}
                          spacing={1.5}
                          alignItems='center'
                        >
                          <Avatar src={row?.profile_pic} alt={row?.name} />
                          <Typography variant="subtitle2">
                            {row?.name}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Scrollbar>
                </Box>

                <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                  {((details?.collaborators && details?.collaborators?.length > 0) || (collaborators && (Array.isArray(collaborators) ? collaborators.length > 0 : String(collaborators || '').trim().length > 0))) && (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Collaborators
                      </Typography>
                      {(() => {
                        const rawCollaborators = details?.collaborators ?? collaborators;
                        const collaboratorIds = Array.isArray(rawCollaborators)
                          ? rawCollaborators.map((c) => (typeof c === 'object' && c != null ? String(c?.id ?? c?.user_id ?? c) : String(c))).filter(Boolean)
                          : String(rawCollaborators || '').split(',').map((id) => id.trim()).filter(Boolean);
                        const teamList = allTeamMembers || collaboratorsTeamMembers || [];
                        let filteredMembers = teamList.filter((member) =>
                          collaboratorIds.includes(String(member?.id)) || collaboratorIds.includes(String(member?.user_id))
                        ) || [];
                        if (Array.isArray(collaborators) && collaborators.length > 0 && filteredMembers.length === 0 && typeof collaborators[0] === 'object' && collaborators[0] != null && (collaborators[0].first_name != null || collaborators[0].name != null)) {
                          filteredMembers = collaborators.map((c) => ({
                            id: c?.id ?? c?.user_id,
                            user_id: c?.user_id ?? c?.id,
                            first_name: c?.first_name ?? c?.name?.split?.(' ')?.[0] ?? '',
                            last_name: c?.last_name ?? c?.name?.split?.(' ').slice?.(1).join?.(' ') ?? '',
                            profile_pic: c?.profile_pic,
                          }));
                        }
                        const currentUserId = String(user?.user_id || user?.id || '')
                        if (currentUserId && collaboratorIds.includes(currentUserId) && !filteredMembers.some((m) => String(m?.id) === currentUserId || String(m?.user_id) === currentUserId)) {
                          filteredMembers = [{ id: user?.user_id || user?.id, user_id: user?.user_id || user?.id, first_name: user?.first_name || user?.name?.split?.(' ')?.[0] || '', last_name: user?.last_name || (user?.name?.split?.(' ').slice?.(1).join?.(' ') || ''), profile_pic: user?.profile_pic }, ...filteredMembers]
                        }
                        const maxVisible = 4
                        const visibleMembers = filteredMembers?.slice(0, maxVisible)
                        const remainingCount = filteredMembers?.length > maxVisible ? filteredMembers.length - maxVisible : 0
                        const membersToRender = showAllCollaborators ? filteredMembers : visibleMembers
                        return (
                          <Stack direction="row" alignItems="center">
                            {membersToRender?.map((row, index) => {
                              const isOnline = onlineUsers?.includes(String(row?.user_id || row?.id))
                              return (
                                <Box key={row?.id ?? row?.user_id ?? index} sx={{ ml: index === 0 ? 0 : -1.5, zIndex: 100 - index, position: 'relative', overflow: 'hidden', borderRadius: '50%' }}>
                                  <Tooltip title={`${row?.first_name || ''} ${row?.last_name || ''}`.trim() || row?.name} arrow>
                                    <Badge
                                      overlap="circular"
                                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                      variant="dot"
                                      sx={{ '& .MuiBadge-badge': { backgroundColor: isOnline ? '#44b700' : '#bdbdbd', boxShadow: '0 0 0 2px white', height: 10, minWidth: 10, maxWidth: 10, borderRadius: '50%', zIndex: 200, overflow: 'hidden' } }}
                                    >
                                      <Avatar src={row?.profile_pic} alt={`${row?.first_name || ''} ${row?.last_name || ''}`} sx={{ width: 42, height: 42, fontSize: 14, border: '2px solid #fff', cursor: 'pointer', bgcolor: 'primary.main', '&:hover': { transform: 'scale(1.1)', zIndex: 300, boxShadow: 3 } }}>{(row?.first_name || row?.name?.split?.(' ')?.[0] || '').charAt(0)}</Avatar>
                                    </Badge>
                                  </Tooltip>
                                </Box>
                              )
                            })}
                            {remainingCount > 0 && (
                              <Box sx={{ ml: -1.5 }}>
                                <Tooltip title={showAllCollaborators ? 'Show less' : `${remainingCount} more collaborators`} arrow>
                                  <Avatar onClick={() => setShowAllCollaborators((s) => !s)} sx={{ width: 42, height: 42, fontSize: 14, border: '2px solid #fff', bgcolor: 'grey.400', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{showAllCollaborators ? '−' : `+${remainingCount}`}</Avatar>
                                </Tooltip>
                              </Box>
                            )}
                          </Stack>
                        )
                      })()}
                    </>
                  )}
                </Box>
              </Stack>
            </>
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          sx={{ justifyContent: 'space-between' }}
          spacing={{ xs: 3, sm: 2 }}
        >
          <Box sx={{width:'80%'}}>
            <Stack sx={{flexDirection:'row'}}>
            <Tooltip title='Description'>
              <Description />
            </Tooltip>
            <Typography variant="body2" sx={{ color: theme.palette.mode === 'light' ? '#000000' : '#ffffff', flexWrap:'wrap', ml:2, whiteSpace: 'pre-line' }}>
              {description}
            </Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          // sx={{ justifyContent: 'space-between' }}
          spacing={{ xs: 3, sm: 2 }}
        >
          {notes ?
          <>
            <Tooltip title='Notes'>
              <Notes sx={{color: 'error.main'}}/>
            </Tooltip>
            <Typography variant="caption" sx={{ flexWrap:'wrap',color: 'error.main', whiteSpace: 'pre-line' }}>
              {notes}
            </Typography> 
          </> : ''}
        </Stack>

        {designOrderProducts?.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Products
            </Typography>
            <TableContainer sx={{ minWidth: 420, mt: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sl No.</TableCell>
                    <TableCell>PRODUCT NAME</TableCell>
                    <TableCell>DESCRIPTION</TableCell>
                    <TableCell align="center">ATTACHMENTS</TableCell>
                    <TableCell align="center">UNIT PRICE EACH</TableCell>
                    <TableCell align="center">QTY</TableCell>
                    <TableCell align="center">TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedDesignOrderDisplayRows?.map((row, index) => {
                    const displaySlNo = index + 1

                    if (row?.__type === 'group') {
                      const representative = row?.representative || {}
                      const groupName =
                        row?.groupMeta?.group_name ||
                        row?.groupMeta?.name ||
                        representative?.group_name ||
                        `Group ${row?.groupId}`
                      const groupDescription =
                        row?.groupMeta?.group_description ||
                        row?.groupMeta?.description ||
                        representative?.group_description ||
                        '-'
                      const members = row?.members || []
                      const groupProductDisplayQty =
                        Number(row?.groupProductDisplayQty ?? 1) || 1
                      const groupInstallDisplayQty =
                        Number(row?.groupInstallDisplayQty ?? 1) || 1
                      const groupInstallSubtotal = Number(row?.groupInstallSubtotal ?? 0)
                      const groupHasInstall = Boolean(row?.groupHasInstall)
                      const displayGroupTotal = groupHasInstall
                        ? Number(row?.groupProductOnlyTotal ?? 0)
                        : Number(row?.totalValue || 0)
                      const unitEach = Number(row?.adjustedGroupRetailEach ?? 0)
                      const groupInstallUnitEach = Number(
                        row?.adjustedGroupInstallRetailEach ?? 0,
                      )
                      const groupInstallNotes = getGroupInstallNotesDisplay(members)
                      const groupDisplayNote =
                        members.find(
                          (m) =>
                            m?.product_notes != null &&
                            String(m.product_notes).trim() !== '',
                        )?.product_notes || ''
                      const showGroupPricing = designOrderProductShowsPricing(representative)
                      const productAttachmentKey = getProductAttachmentKey(representative)
                      const itemAttachments = getGroupMemberAttachments(members)
                      const { isGroupProductLosingMoney, isGroupInstallLosingMoney } =
                        getDesignOrderDetailsGroupLosingMoneyFlags(row)
                      const productGroupRowSx = getDesignOrderLosingMoneyRowSx(
                        theme,
                        (isGroupProductLosingMoney || isGroupInstallLosingMoney),
                      )
                      const installGroupRowSx = getDesignOrderLosingMoneyRowSx(
                        theme,
                        (isGroupInstallLosingMoney || isGroupProductLosingMoney),
                      )

                      return (
                        <Fragment key={`design-order-group-${row?.groupId}`}>
                          <TableRow
                            onDragEnterCapture={(e) => handleProductIconDragOver(e, representative)}
                            onDragOverCapture={(e) => handleProductIconDragOver(e, representative)}
                            onDragLeave={(e) => handleProductIconDragLeave(e, representative)}
                            onDropCapture={(e) => handleProductIconDrop(e, representative)}
                            sx={{
                              ...productGroupRowSx,
                              ...(productAttachmentDragOver[productAttachmentKey] && {
                                backgroundColor: 'action.hover',
                                outline: `2px dashed ${theme.palette.primary.main}`,
                                outlineOffset: -2,
                              }),
                            }}
                          >
                            <TableCell>{displaySlNo}</TableCell>
                            <TableCell width="25%" sx={{ flexWrap: 'wrap', overflow: 'hidden' }}>
                              <Tooltip title={groupName}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    maxWidth: 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {groupName}
                                </Typography>
                              </Tooltip>
                              {groupDisplayNote ? (
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'pre-line',
                                    color: 'error.main',
                                  }}
                                >
                                  Note: {groupDisplayNote}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell width="25%" sx={{ flexWrap: 'wrap', overflow: 'hidden' }}>
                              <Tooltip title={groupDescription}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'bold',
                                    mb: 1,
                                    maxWidth: 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {groupDescription}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              {canEditProductAttachments(representative) ? (
                                <Tooltip title="Drop files on row or click to open attachments">
                                  <IconButton onClick={() => handleAddAttachment(representative)}>
                                    {isUploadingProduct[productAttachmentKey] ? (
                                      <CircularProgress size={34} />
                                    ) : (
                                      <UploadFile
                                        sx={{
                                          color:
                                            itemAttachments.length === 0
                                              ? 'error.main'
                                              : theme.palette.primary.main,
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <AttachmentDownloadIconButton
                                  file={itemAttachments[0] || null}
                                  hidden={isClient || userType === 3}
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {showGroupPricing ? `$${fUSPrice(unitEach)}` : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {showGroupPricing ? groupProductDisplayQty : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {showGroupPricing ? `$${fUSPrice(displayGroupTotal)}` : '-'}
                            </TableCell>
                          </TableRow>
                          {groupHasInstall ? (
                            <TableRow sx={installGroupRowSx}>
                              <TableCell />
                              <TableCell sx={{ pl: 4 }}>
                                <Typography>Install Price</Typography>
                                {groupInstallNotes ? (
                                  <Typography sx={{ color: 'error.main', whiteSpace: 'pre-wrap' }}>
                                    Note: {groupInstallNotes}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell>-</TableCell>
                              <TableCell align="center">-</TableCell>
                              <TableCell align="center">
                                {showGroupPricing ? `$${fUSPrice(groupInstallUnitEach)}` : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {showGroupPricing ? groupInstallDisplayQty : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {showGroupPricing
                                  ? `$${fUSPrice(groupInstallSubtotal)}`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          ) : null}
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Box sx={{ borderBottom: (t) => `solid 1px ${t.palette.divider}` }} />
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      )
                    }

                    const item = row
                    const productName = item?.name || item?.product_name || '-'
                    const description = item?.product_desc || item?.description || '-'
                    const productNotes = item?.product_notes
                    const showPricing = designOrderProductShowsPricing(item)
                    const unitPrice = getDesignOrderProductUnitPrice(item)
                    const qty = getDesignOrderProductQty(item)
                    const productSubtotal = getDesignOrderProductSubtotal(item)
                    const installCost = parseFloat(item?.install_cost) || 0
                    const installQty = Number(item?.install_quantity ?? 1)
                    const productRowTotal = installCost > 0
                      ? productSubtotal
                      : getDesignOrderProductGrandTotal(item)
                    const productAttachmentKey = getProductAttachmentKey(item)
                    const itemAttachments = productAttachments[productAttachmentKey] || []
                    const { isLosingMoneyItem } = getDesignOrderProductLosingMoneyFlags(item)
                    const losingMoneyRowSx = getDesignOrderLosingMoneyRowSx(theme, isLosingMoneyItem)

                    return (
                      <Fragment key={item?.id ?? `design-order-product-${displaySlNo}-${index}`}>
                        <TableRow
                          onDragEnterCapture={(e) => handleProductIconDragOver(e, item)}
                          onDragOverCapture={(e) => handleProductIconDragOver(e, item)}
                          onDragLeave={(e) => handleProductIconDragLeave(e, item)}
                          onDropCapture={(e) => handleProductIconDrop(e, item)}
                          sx={{
                            ...losingMoneyRowSx,
                            ...(productAttachmentDragOver[productAttachmentKey] && {
                              backgroundColor: 'action.hover',
                              outline: `2px dashed ${theme.palette.primary.main}`,
                              outlineOffset: -2,
                            }),
                          }}
                        >
                          <TableCell>{displaySlNo}</TableCell>
                          <TableCell width="25%" sx={{ flexWrap: 'wrap', overflow: 'hidden' }}>
                            <Tooltip title={productName}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 'bold',
                                mb: 1,
                                maxWidth: 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap', // must stay single-line
                              }}
                            >
                              {productName}
                            </Typography>
                            </Tooltip>
                            {productNotes ? (
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 'bold',
                                  mb: 1,
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  whiteSpace: 'pre-line',
                                  color: 'error.main',
                                }}
                              >
                                Note: {productNotes}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell width="25%" sx={{ flexWrap: 'wrap', overflow: 'hidden' }}>
                            <Tooltip title={description}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 'bold',
                                  mb: 1,
                                  maxWidth: 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap', // must stay single-line
                                }}
                              >
                                {description}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                          {canEditProductAttachments(item) ? (
                            <Tooltip title="Drop files on row or click to open attachments">
                              <IconButton onClick={() => handleAddAttachment(item)}>
                                {isUploadingProduct[productAttachmentKey] ? (
                                  <CircularProgress size={34} />
                                ) : (
                                  <UploadFile
                                    sx={{
                                      color:
                                        itemAttachments.length === 0
                                          ? 'error.main'
                                          : theme.palette.primary.main,
                                    }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <AttachmentDownloadIconButton
                              file={itemAttachments[0] || null}
                              hidden={isClient || userType === 3}
                            />
                          )}
                          </TableCell>
                          <TableCell align="center">
                            {showPricing ? `$${fUSPrice(unitPrice)}` : '-'}
                          </TableCell>
                          <TableCell align="center">{showPricing ? qty : '-'}</TableCell>
                          <TableCell align="center">
                            {showPricing ? `$${fUSPrice(productRowTotal)}` : '-'}
                          </TableCell>
                        </TableRow>
                        {installCost > 0 ? (
                          <TableRow sx={losingMoneyRowSx}>
                            <TableCell/>
                            <TableCell sx={{ pl: 4 }}>
                              <Typography>Install Price</Typography>
                              {item?.install_notes ? (
                                <Typography sx={{ color: 'error.main', whiteSpace: 'pre-wrap' }}>
                                  Note: {item.install_notes}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell>-</TableCell>
                            <TableCell align="center">-</TableCell>
                            <TableCell align="center">${fUSPrice(installCost)}</TableCell>
                            <TableCell align="center">{installQty}</TableCell>
                            <TableCell align="center">
                              ${fUSPrice(installCost * installQty)}
                            </TableCell>
                          </TableRow>
                        ) : null}
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Box sx={{ borderBottom: (t) => `solid 1px ${t.palette.divider}` }} />
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {sortDesignOrderProductsByIndex(designOrderProducts)?.map((item) => {
              const productAttachmentKey = getProductAttachmentKey(item)
              return (
                <Modal
                  key={`design-order-product-attachment-${productAttachmentKey}`}
                  width={900}
                  open={Boolean(openProductAttachment[productAttachmentKey])}
                  handleClose={() => handleCloseAttachment(item)}
                  modalTitle=""
                >
                  {renderProductAttachmentModalContent(item)}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={() => handleCloseAttachment(item)}>
                      Close
                    </Button>
                  </Box>
                </Modal>
              )
            })}
          </>
        )}

        {designOrderServiceRows?.length > 0 && (
          <TableContainer sx={{ minWidth: 420, mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sl No.</TableCell>
                  <TableCell>
                    {details?.job_category_name != 'Service only' &&
                    details?.job_category_name != 'Time & Material'
                      ? 'ADDITIONAL SERVICES'
                      : 'SERVICES'}
                  </TableCell>
                  <TableCell>DESCRIPTION</TableCell>
                  <TableCell>COST (HOURLY)</TableCell>
                  <TableCell>HOURS</TableCell>
                  <TableCell width="15%">TOTAL COST</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {designOrderServiceRows.map((service, index) => {
                  const serviceName = service?.service_name || service?.name || '-'
                  const serviceDescription = service?.service_desc || service?.description || '-'
                  const serviceNotes = service?.service_notes
                  const unitCost = getDesignOrderServiceUnitCost(service)
                  const serviceQty = getDesignOrderServiceQuantity(service)
                  const serviceTotal = getDesignOrderServiceTotal(service)

                  return (
                    <TableRow
                      key={service?.id ?? `design-order-service-${index}`}
                      sx={{ borderBottom: (t) => `solid 1px ${t.palette.divider}` }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography>{serviceName}</Typography>
                          {serviceNotes ? (
                            <Stack direction="column">
                              <Typography
                                variant="body2"
                                sx={{
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  color: 'error.main',
                                  whiteSpace: 'pre-line',
                                }}
                              >
                                Note :{serviceNotes}
                              </Typography>
                            </Stack>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {serviceDescription !== '-' ? (
                          <Tooltip title={serviceDescription} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '150px',
                                cursor: 'pointer',
                              }}
                            >
                              {serviceDescription}
                            </Typography>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>${fUSPrice(unitCost)}</TableCell>
                      <TableCell>{serviceQty.toFixed(2)}</TableCell>
                      <TableCell>${fUSPrice(serviceTotal)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {showDesignOrderSummary && (
          <TableContainer sx={{ minWidth: 420, mt: (designOrderProducts?.length > 0 || designOrderServiceRows?.length > 0) ? 1 : 3 }}>
            <Table>
              <TableBody>
                {productTotal > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} />
                    <TableCell align="right">
                      <Typography variant="overline">Product Total</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="overline">${fUSPrice(productTotal)}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {installTotal > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} />
                    <TableCell align="right">
                      <Typography variant="overline">Install Total</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="overline">${fUSPrice(installTotal)}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {additionalServicesTotal > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} />
                    <TableCell align="right">
                      <Typography variant="overline">
                        {details?.job_category_name != 'Service only'
                          ? 'Additional Services Total'
                          : 'Services Total'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="overline">${fUSPrice(additionalServicesTotal)}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={4} />
                  <TableCell align="right">
                    <Typography variant="overline">Subtotal</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="overline">${fUSPrice(designOrderSubtotal)}</Typography>
                  </TableCell>
                </TableRow>
                {designOrderDiscount > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} />
                    <TableCell align="right">
                      <Typography variant="overline">Discount</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="overline">${fUSPrice(designOrderDiscount)}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {designOrderTax > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} />
                    <TableCell align="right">
                      <Typography variant="overline">Tax</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="overline">${fUSPrice(designOrderTax)}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={4} />
                  <TableCell align="right">
                    <Typography
                      variant="subtitle1"
                      sx={{ color: designOrderTotal < 0 ? 'red' : 'inherit' }}
                    >
                      Total
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="subtitle1"
                      sx={{ color: designOrderTotal < 0 ? 'red' : 'inherit' }}
                    >
                      ${fUSPrice(designOrderTotal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`, // light border
            borderRadius: "8px",
            p: "4px",
            mb: 2,
          }}
        >
          <StyledTabs
            value={activeTabNew}
            onChange={(e, val) => setActiveTabNew(val)}
            centered
          >
            <StyledTab label="Designs / Files" value="designs" />
            <StyledTab label="References" value="references" />
          </StyledTabs>

          {/* <Divider sx={{ mt: 1 }} /> */}
        </Box>
        {activeTabNew === 'designs' &&
          !user.isVendor &&
          !isClient &&
          !project && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 2, sm: 3 }}
            mb={2}
          >
            {((designStatus !== 2 && send_client_app_req === 0) || designStatus === 6) && (
              <>
                <LoadingButton
                  variant="outlined"
                  size="small"
                  sx={{ height: 40 }}
                  onClick={() => {
                    const explodedPermissions = user?.exploded_permissions
                    const designOrderPermission = !explodedPermissions?.some((permission) =>
                      ['Add Design to Design Order Job'].includes(permission),
                    )
                    if (designOrderPermission && user?.added_by_client == 1) {
                      return enqueueSnackbar(
                        'Access Denied: You do not have sufficient permissions',
                        { variant: 'error' },
                      )
                    }
                    fileInputRef.current.click()
                  }}
                  loading={progress}
                >
                  Upload Designs / Documents
                </LoadingButton>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  multiple
                  style={{ display: 'none' }}
                />
              </>
            )}
            <AttachmentDownloadAllButton
              files={(filteredDesigns || []).filter((item) =>
                selectedDesigns.includes(item.id),
              )}
              allFiles={filteredDesigns || []}
              onToggleSelectAll={(checked) => {
                setSelectedDesigns(
                  checked
                    ? (filteredDesigns || []).map((item) => item.id).filter(Boolean)
                    : [],
                )
              }}
              onEmptyClick={() => {
                setDownloadSelectMode(true)
                enqueueSnackbar(
                  'Click designs to select, then press Download Selected again',
                  { variant: 'info' },
                )
              }}
              selectionMode={downloadSelectMode}
              label="Download Selected"
              hidden={isClient || userType === 3}
              size="small"
              sx={{ height: 40 }}
            />
          </Stack>
        )}
        {(activeTabNew === 'references') ?
          ((referenceFiles?.length > 0) ? (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={{ xs: 2, sm: 3 }}
                mb={2}
              >
                <AttachmentDownloadAllButton
                  files={(referenceFiles || []).filter((item) =>
                    selectedReferences.includes(item.id || item.name),
                  )}
                  allFiles={referenceFiles || []}
                  onToggleSelectAll={(checked) => {
                    setSelectedReferences(
                      checked
                        ? (referenceFiles || [])
                            .map((item) => item.id || item.name)
                            .filter(Boolean)
                        : [],
                    )
                  }}
                  onEmptyClick={() => {
                    setReferenceDownloadSelectMode(true)
                    enqueueSnackbar(
                      'Click references to select, then press Download Selected again',
                      { variant: 'info' },
                    )
                  }}
                  selectionMode={referenceDownloadSelectMode}
                  label="Download Selected"
                  hidden={isClient || userType === 3}
                  size="small"
                  sx={{ height: 40 }}
                />
              </Stack>
            <Box sx={{border: '0.1px solid', borderColor: borderColor,padding:2, }}>
              <Scrollbar sx={{ width: '100%', height: 450 }}>
                {referenceFiles?.length > 0 ?
                  <ImageList
                    gap={28}
                    cols={xs ? 1 : 4}
                    sx={{
                      gridTemplateColumns: {
                        xs: 'repeat(1, minmax(0, 1fr)) !important',
                        sm: 'repeat(2, minmax(0, 1fr)) !important',
                        md: 'repeat(4, minmax(0, 1fr)) !important',
                      },
                    }}
                  >
                    {referenceFiles?.map((item,index) =>{
                      const { file_type, name, url, extension, approved, reason, status, id } = item;
                      const referenceKey = id || name
                      const isReferenceSelected = selectedReferences.includes(referenceKey)
                      const handleToggleReference = () => {
                        setSelectedReferences((prev) =>
                          isReferenceSelected
                            ? prev.filter((n) => n !== referenceKey)
                            : [...prev, referenceKey],
                        )
                      }
                      const fileExtStr = (extension || getFileExtension(name) || '').toLowerCase();
                      const isGoogleFormat = ['eps','ai','psd','tif','tiff','ps','indd','indt'].includes(fileExtStr);
                      const isImage = (file_type?.startsWith('image/') || file_type ==='img' || file_type ==='image') && !isGoogleFormat;
                      const isActualPdf = file_type === 'application/pdf' || file_type === "pdf" || extension == 'pdf' || getFileExtension(name) === 'pdf';
                      const isPdf = !isImage;
                      return (
                        <ImageListItem
                          key={item.name}
                          onClick={() => {
                            if (!referenceDownloadSelectMode) return
                            handleToggleReference()
                          }}
                          sx={{
                            cursor: referenceDownloadSelectMode ? 'pointer' : 'pointer',
                            position: 'relative',
                            width: '100%',
                            maxWidth: '100%',
                            height: 'auto',
                            overflow: 'hidden',
                            alignSelf: 'start',
                            border: referenceDownloadSelectMode && isReferenceSelected ? '2px solid' : '1px solid',
                            borderColor:
                              referenceDownloadSelectMode && isReferenceSelected
                                ? 'primary.main'
                                : 'transparent',
                            outline: referenceDownloadSelectMode
                              ? (theme) => `2px dashed ${theme.palette.primary.main}55`
                              : 'none',
                          }}
                        >
                          <Box position="relative" sx={{ width: '100%' }}>
                            <Checkbox
                              checked={isReferenceSelected}
                              onChange={handleToggleReference}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 12,
                                color: 'black',
                                backgroundColor: theme.palette.grey.A700,
                                boxShadow: 1,
                              }}
                            />
                            <AttachmentDownloadIconButton
                              file={{ ...item, url: url || name }}
                              fileIndex={index}
                              hidden={isClient || userType === 3}
                              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 12 }}
                            />
                            {isImage ? (
                              <a
                              // href={name}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault();
                                if (referenceDownloadSelectMode) {
                                  handleToggleReference()
                                  return
                                }
                                handleImageClickReference(referenceFiles.findIndex((img) => img.name === name))
                              }}
                            >
                              <img
                                srcSet={`${item.name}?w=240&h=240&fit=crop&auto=format&dpr=2 2x`}
                                src={`${item.name}?w=240&h=240&fit=crop&auto=format`}
                                alt={item.title}
                                loading="lazy"
                                style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }}
                              />
                              </a>
                            ) : isPdf ? (
                              <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                              {/* PDF Icon Button */}
                              <Tooltip title={isActualPdf ? 'View PDF' : 'View 1'}>
                                <IconButton 
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (referenceDownloadSelectMode) {
                                    handleToggleReference()
                                    return
                                  }
                                  openFilePreview(item)
                                }}
                                  aria-label="open pdf"
                                  sx={{ position:'absolute', 
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 200,color:theme.palette.error.main, backgroundColor:theme.palette.text.primary }} >
                                  {isActualPdf ? <PictureAsPdf style={{ fontSize: 40 }} /> : <Description style={{ fontSize: 40 }} />}
                                </IconButton>
                              </Tooltip>
                        
                              {/* Thumbnail */}
                              {isActualPdf ? (
                                <iframe
                                  src={name}
                                  title={name}
                                  style={{ width: '100%', height: '240px' }}
                                  frameBorder="0"
                                />
                              ) : (() => {
                                const fileExt = getFileExtension(name) || (extension || '').toLowerCase();
                                const displayName =
                                  resolveDownloadFileName(name) || 'File';
                                const isOfficeDoc = isOfficeAttachment(name, file_type) ||
                                  ['xls','xlsx','doc','docx','ppt','pptx','csv'].includes(fileExt);
                                const isGoogleFile = ['ai', 'eps', 'ps', 'psd', 'tif', 'tiff', 'indd', 'indt'].includes(fileExt);
                                
                                if (isOfficeDoc) {
                                  return (
                                    <iframe
                                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(name)}`}
                                      title={displayName}
                                      style={{ width: '100%', height: '240px', borderRadius: '4px' }}
                                      frameBorder="0"
                                    />
                                  );
                                }
                                
                                if (isGoogleFile) {
                                  return (
                                    <iframe
                                      src={`https://docs.google.com/gview?url=${encodeURIComponent(name)}&embedded=true`}
                                      title={displayName}
                                      style={{ width: '100%', height: '240px', borderRadius: '4px' }}
                                      frameBorder="0"
                                      sandbox="allow-scripts allow-same-origin"
                                    />
                                  );
                                }
                                
                                return (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '240px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: 'rgba(255,255,255,0.05)',
                                      borderRadius: 1,
                                      border: '1px dashed rgba(255,255,255,0.2)',
                                      gap: 1,
                                    }}
                                  >
                                    <Description style={{ fontSize: 60, color: theme.palette.text.secondary }} />
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textAlign: 'center', px: 1, wordBreak: 'break-all' }}>
                                      {displayName}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </Box>
                            ) : null}
                            <ImageListItemBar
                            sx={{
                              width: '100%',
                            }}
                              subtitle={
                                isPdf ? (
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption">
                                      {moment.unix(item.timestamp).format('MM/DD/YYYY')}
                                    </Typography>
                                    {isActualPdf && (
                                    <Accordion
                                      square
                                      disableGutters
                                      elevation={0}
                                      sx={{
                                        backgroundColor: 'transparent',
                                        '&:before': { display: 'none' },
                                        minHeight: '18px',
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'inherit' }} />}
                                        sx={{
                                          minHeight: '18px',
                                          px: 0,
                                          '&.Mui-expanded': { minHeight: '18px' },
                                          '& .MuiAccordionSummary-content': { margin: 0 },
                                          '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                                        }}
                                      >
                                        {isActualPdf && (
                                          <Typography variant="caption">Edit PDF</Typography>
                                        ) }
                                        {isActualPdf && !isImage && (
                                          <Typography variant="caption">Edit File</Typography>
                                        )}
                                      </AccordionSummary>
                                      <AccordionDetails sx={{ p: 0, pt: 1, pb: 0.5 }}>
                                        <Stack direction={columnView ? "column" : "row"} width="100%" spacing={1} sx={{ flexDirection: columnView ? 'column' : 'row' }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={() => {
                                              setSelectedPDFForEdit({ url: name, item: item });
                                              setOpenPDFEditor(true);
                                            }}
                                            startIcon={<EditIcon sx={{ fontSize: 11 }} />}
                                            sx={{ textTransform: 'none', boxShadow: 'none', fontSize: '11px' }}
                                          >
                                            Edit
                                          </Button>
                                          {/* <Button
                                            size="small"
                                            variant="outlined"
                                            color="inherit"
                                            onClick={() => handleDownloadEditedPdf(item.id)}
                                            sx={{ textTransform: 'none', fontSize: '11px' }}
                                          >
                                            Download Edited PDF
                                          </Button> */}
                                        </Stack>
                                      </AccordionDetails>
                                    </Accordion>
                                    )}
                                  </Stack>
                                ) : (
                                  <Typography variant="caption">
                                    {moment.unix(item.timestamp).format('MM/DD/YYYY')}
                                  </Typography>
                                )
                              }
                            />
                          </Box>
                        </ImageListItem>
                      )
                    })}
                  </ImageList>
                : '' }
              </Scrollbar>
            </Box>
            </>
          ) : 
          <Stack alignItems={'center'} justifyContent={'center'} >
            <Typography>
              No References Found
            </Typography>
          </Stack>
        ) : (
          <>
          {userType !==3 ? ((details?.send_client_app_req && designs?.length > 0) ? (
            <Box display="flex" justifyContent="center" mb={2} gap={2} alignItems="center">
              <Button
                variant={activeTab === 'designs' ? 'contained' : 'outlined'}
                onClick={() => setActiveTab('designs')}
              >
                Designs
              </Button>
              <Button
                variant={activeTab === 'files' ? 'contained' : 'outlined'}
                onClick={() => setActiveTab('files')}
              >
                Files
              </Button>
              {selectedDesigns.length > 0 && (
                <Tooltip title={`Send ${selectedDesigns.length} design${selectedDesigns.length !== 1 ? 's' : ''} to email`}>
                  <Badge badgeContent={selectedDesigns.length} color="error">
                    <IconButton
                      color="primary"
                      onClick={handleSendSelectedDesignsToEmail}
                      sx={{
                        ml: 2,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      }}
                    >
                      <Icon icon={emailFillIcon} width={24} height={24} />
                    </IconButton>
                  </Badge>
                </Tooltip>
              )}
            </Box>
          ) : (
            selectedDesigns.length > 0 && (
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Tooltip title={`Send ${selectedDesigns.length} design${selectedDesigns.length !== 1 ? 's' : ''} to email`}>
                  <Badge badgeContent={selectedDesigns.length} color="error">
                    <IconButton
                      color="primary"
                      onClick={handleSendSelectedDesignsToEmail}
                      sx={{
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      }}
                    >
                      <Icon icon={emailFillIcon} width={24} height={24} />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </Box>
            )
          )) : (
            selectedDesigns.length > 0 && (
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Tooltip title={`Send ${selectedDesigns.length} design${selectedDesigns.length !== 1 ? 's' : ''} to email`}>
                  <Badge badgeContent={selectedDesigns.length} color="error">
                    <IconButton
                      color="primary"
                      onClick={handleSendSelectedDesignsToEmail}
                      sx={{
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      }}
                    >
                      <Icon icon={emailFillIcon} width={24} height={24} />
                    </IconButton>
                  </Badge>
                </Tooltip>
              </Box>
            )
          )}
          {(userType === 3 ? (details?.send_client_app_req || approvedDesigns?.length > 0) : designs?.length >0) ? (
            <Box sx={{border: '0.1px solid', borderColor: borderColor,padding:2, }}>
              <Scrollbar sx={{ width: '100%', height: 450 }}>
              {filteredDesigns?.length > 0 ?
                <ImageList
                  gap={28}
                  cols={xs ? 1 : 4}
                  sx={{
                    gridTemplateColumns: {
                      xs: 'repeat(1, minmax(0, 1fr)) !important',
                      sm: 'repeat(2, minmax(0, 1fr)) !important',
                      md: 'repeat(4, minmax(0, 1fr)) !important',
                    },
                  }}
                >
                  {/* <ImageListItem key="Subheader" cols={2}> */}
                  {/* </ImageListItem> */}
                  {filteredDesigns?.map((item,index) =>{
                      // const { file_type, name, url } = item;
                      // const isImage = file_type?.startsWith('image/') || file_type ==='img';
                      // const isPdf = file_type === 'application/pdf';
                      const { file_type, name, url, extension, approved, reason, status, id } = item;
                      if (item.approved !== 1 && isClient) return;
                      const fileExtStr = (extension || getFileExtension(name) || '').toLowerCase();
                      const isGoogleFormat = ['eps','ai','psd','tif','tiff','ps','indd','indt'].includes(fileExtStr);
                      const isImage = (file_type?.startsWith('image/') || file_type ==='img' || file_type ==='image') && !isGoogleFormat;
                      const isActualPdf = file_type === 'application/pdf' || file_type === "pdf" || extension == 'pdf' || getFileExtension(name) === 'pdf';
                      const isPdf = !isImage;
                      const isSelected = selectedDesigns?.includes(id);
                      const handleToggle = () => {
                        setSelectedDesigns((prev) =>
                          isSelected ? prev?.filter((n) => n !== id) : [...prev, id],
                        )
                      }
                      const linkedCatalogProduct = getLinkedCatalogProductForDesign(item)
                      const catalogLabel = linkedCatalogProduct
                        ? getDesignOrderDetailsCatalogLinkLabel(
                            linkedCatalogProduct,
                            resolvedProductGroupsMap,
                          )
                        : ''
                      const isCatalogLinked = !!item?.design_order_product_id
                    return (
                    <ImageListItem
                      key={item.name}
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                        overflow: 'hidden',
                        alignSelf: 'start',
                        border: '1px solid',
                        borderColor:
                          ((item.approved === 1 && isImage) ||
                            (item.approved === 1 && isPdf && isCatalogLinked))
                            ? theme.palette.success.main
                            : item.reason
                              ? theme.palette.error.main
                              : isCatalogLinked
                                ? theme.palette.info.main
                                : theme.palette.divider,
                      }}
                    >
                      <Stack
                        sx={{
                          position: 'absolute',
                          zIndex: 9,
                          top: 8,
                          left: 10,
                        }}
                      >
                        {((item.approved === 1 && isImage) ||
                          (item.approved === 1 && isPdf && isCatalogLinked)) ? (
                          isCatalogLinked ? (
                            <Tooltip title={`Linked with: ${catalogLabel}`} arrow>
                              <span>
                                <Label
                                  variant="filled"
                                  color="success"
                                  style={{ color: 'white', height: 22, width: 200 }}
                                >
                                  Linked with Catalog & Approved
                                </Label>
                              </span>
                            </Tooltip>
                          ) : (
                            <Label
                              variant="filled"
                              color="success"
                              style={{ color: 'white', height: 22, width: 110 }}
                            >
                              Approved
                            </Label>
                          )
                        ) : isCatalogLinked ? (
                          <Tooltip title={`Linked with: ${catalogLabel}`} arrow>
                            <span>
                              <Label
                                variant="filled"
                                color="info"
                                style={{ color: 'white', height: 20 }}
                              >
                                {(() => {
                                  const prefix = 'Linked with: '
                                  const text = prefix + catalogLabel
                                  return text.length > 15
                                    ? text.substring(0, 12) + '...'
                                    : text
                                })()}
                              </Label>
                            </span>
                          </Tooltip>
                        ) : item.reason ? (
                          <Tooltip title={`Reason: ${item.reason}`}>
                            <span>
                              <Label
                                variant="filled"
                                color="error"
                                style={{ color: 'white', height: 22, width: 110 }}
                              >
                                Rejected
                              </Label>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Stack>
                      <Box position="relative" sx={{ width: '100%' }}>
                        <AttachmentDownloadIconButton
                          file={{ ...item, url: url || name }}
                          fileIndex={index}
                          hidden={isClient || userType === 3}
                          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 12 }}
                        />
                        <Checkbox
                          checked={isSelected}
                          onChange={handleToggle}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 10,
                            color: 'black',
                            backgroundColor: theme.palette.grey.A700,
                            boxShadow: 1,
                          }}
                        />
                          {isImage ? (
                            <a
                            // href={name}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.preventDefault();
                              handleImageClick(imageDesigns.findIndex((img) => img.name === name))
                            }}
                          >
                            <img
                              // srcSet={`https://gratisography.com/wp-content/uploads/2025/01/gratisography-dog-vacation-1170x780.jpg?w=240&h=240&fit=crop&auto=format&dpr=2 2x`}
                              // src={`https://gratisography.com/wp-content/uploads/2025/01/gratisography-dog-vacation-1170x780.jpg?w=240&h=240&fit=crop&auto=format`}
                              srcSet={`${item.name}?w=240&h=240&fit=crop&auto=format&dpr=2 2x`}
                              src={`${item.name}?w=240&h=240&fit=crop&auto=format`}
                              alt={item.title}
                              loading="lazy"
                              style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }}
                            />
                            </a>
                          ) : isPdf ? (
                            <Box display="flex" alignItems="center">
                            {/* PDF Icon Button */}
                            <Tooltip title={isActualPdf ? 'View PDF' : 'View File'}>
                              <IconButton 
                              onClick={() => openFilePreview(item)}
                                aria-label="open pdf"
                                sx={{ position:'absolute', 
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                zIndex: 200,color:theme.palette.error.main, backgroundColor:theme.palette.text.primary }} >
                                {isActualPdf ? <PictureAsPdf style={{ fontSize: 40 }} /> : <Description style={{ fontSize: 40 }} />}
                              </IconButton>
                            </Tooltip>
                      
                            {/* Thumbnail */}
                              {isActualPdf ? (
                                <iframe
                                  src={name}
                                  title={name}
                                  style={{ width: '100%', height: '240px' }}
                                  frameBorder="0"
                                />
                              ) : (() => {
                                const fileExt = getFileExtension(name) || (extension || '').toLowerCase();
                                const displayName = resolveDownloadFileName(name) || 'File';
                                const isOfficeDoc =
                                  isOfficeAttachment(name, file_type) ||
                                  ['xls','xlsx','doc','docx','ppt','pptx','csv'].includes(fileExt);
                                const isGoogleFile = ['ai', 'eps', 'ps', 'psd'].includes(fileExt);
                                
                                if (isOfficeDoc) {
                                  return (
                                    <iframe
                                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(name)}`}
                                      title={displayName}
                                      style={{ width: '100%', height: '240px', borderRadius: '4px' }}
                                      frameBorder="0"
                                    />
                                  );
                                }

                                if (isGoogleFile) {
                                  return (
                                    <iframe
                                      src={`https://docs.google.com/gview?url=${encodeURIComponent(name)}&embedded=true`}
                                      title={displayName}
                                      style={{ width: '100%', height: '240px', borderRadius: '4px' }}
                                      frameBorder="0"
                                      sandbox="allow-scripts allow-same-origin"
                                    />
                                  );
                                }
                                
                                return (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '240px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: 'rgba(255,255,255,0.05)',
                                      borderRadius: 1,
                                      border: '1px dashed rgba(255,255,255,0.2)',
                                      gap: 1,
                                    }}
                                  >
                                    <Description style={{ fontSize: 60, color: theme.palette.text.secondary }} />
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textAlign: 'center', px: 1, wordBreak: 'break-all' }}>
                                      {displayName}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                          </Box>
                          ) : null}
                      <ImageListItemBar
                      sx={{
                        width: '100%',
                        left: 0,
                        right: 0,
                        bottom: 0,
                      }}
                        subtitle={
                          isPdf ? (
                            <Stack spacing={0.5}>
                              <Typography variant="caption">
                                {moment.unix(item.timestamp).format('MM/DD/YYYY')}
                              </Typography>
                              {isActualPdf && (
                              <Accordion
                                square
                                disableGutters
                                elevation={0}
                                sx={{
                                  backgroundColor: 'transparent',
                                  '&:before': { display: 'none' },
                                  minHeight: '18px',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'inherit' }} />}
                                  sx={{
                                    minHeight: '18px',
                                    px: 0,
                                    '&.Mui-expanded': { minHeight: '18px' },
                                    '& .MuiAccordionSummary-content': { margin: 0 },
                                    '& .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
                                  }}
                                >
                                  <Typography variant="caption">{isActualPdf ? 'Edit PDF' : 'Edit File'}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0, pt: 1, pb: 0.5 }}>
                                  <Stack direction={columnView ? "column" : "row"} width="100%" spacing={1} sx={{ flexDirection: columnView ? 'column' : 'row' }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="primary"
                                      fullWidth
                                      onClick={() => {
                                        setSelectedPDFForEdit({ url: name, item: item });
                                        setOpenPDFEditor(true);
                                      }}
                                      startIcon={<EditIcon sx={{ fontSize: 11 }} />}
                                      sx={{ textTransform: 'none', boxShadow: 'none', fontSize: '11px' }}
                                    >
                                      Edit
                                    </Button>
                                    {/* <Button
                                      size="small"
                                      variant="outlined"
                                      color="inherit"
                                      onClick={() => handleDownloadEditedPdf(item.id)}
                                      sx={{ textTransform: 'none', fontSize: '11px' }}
                                    >
                                      Download Edited PDF
                                    </Button> */}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="caption">
                              {moment.unix(item.timestamp).format('MM/DD/YYYY')}
                            </Typography>
                          )
                        }
                        // actionIcon={
                        //    <>                       
                        //    {!user.isVendor&& !project&&(
                        //   <UserMoreMenu
                        //     common={{
                        //       name: (item?.approved !== 1 && item?.product_id !== null) && 'Approve',
                        //       function:
                        //       (item?.approved !== 1 && item?.product_id !== null) ? () => {
                        //         setItemId(item.id)
                        //         setModalClose(true)
                        //         handleUpdateDesign({
                        //           id: item.id,
                        //           // product_id: product.product_id,
                        //           approved: 1,
                        //           reason:null
                        //         })
                        //         // setOpenProductModel(true)
                        //       } : null ,
                        //       Iccon:
                        //       (item?.approved !== 1 && item?.product_id !== null) && (
                        //           <ThumbUpAltOutlined />
                        //         ),
                        //       modalClose: modalClose,
                        //     }}
                        //     linkImageWithTask={{
                        //       name: item?.product_id == null && 'Link with catalog',
                        //       function:
                        //       item?.product_id == null ? () => {
                                      
                        //               setItemId(item.id)
                        //               setModalClose(true)
                        //               setOpenProductModel(true)
                        //             } : null ,
                        //       Iccon: item?.product_id == null && (
                        //           <Link />
                        //         ),
                        //       modalClose: modalClose,
                        //     }}

                        //     common2={{
                        //       name: (item?.approved === 1 || (item?.approved === 0 && item?.reason === null)) && 'Reject',
                        //       function:
                        //       (item?.approved === 1 || (item?.approved === 0 && item?.reason === null)) ? () => {
                        //                 setItemId(item.id)
                        //                 setOpenReasonModal(true)
                        //             } : null ,
                        //       Iccon:
                        //       (item?.approved === 1 || (item?.approved === 0 && item?.reason === null)) && (
                        //           <ThumbDownOffAltOutlined />
                        //         )
                        //         ,
                        //       modalClose: modalClose,
                        //     }}
                        //     //   onDelete={() => handleUpdateDesign({ id: item.id, status: 0 })}
                        //     {...(item.approved === 1
                        //       ? {}
                        //       : {
                        //           onDelete: () =>
                        //             handleUpdateDesign({
                        //               id: item.id,
                        //               status: 0,
                        //             }),
                        //         })}
                        //   />
                        //   )}                          
                        //   </>
                        // }
                        actionIcon={
                            <>
                              {!user.isVendor && !project && (designStatus !==2 && send_client_app_req === 0 || designStatus ===6 || item?.isDesign === 2) && (
                                <UserMoreMenu
                                  linkImageWithTask={
                                    linkableCatalogProducts?.length > 0 && !item?.design_order_product_id && !isClient ? {
                                      name:
                                        !item?.design_order_product_id && item?.reason == null && (isImage || isPdf)
                                          ? 'Link with catalog'
                                          : '',
                                      function:
                                        !item?.design_order_product_id && (isImage || isPdf)
                                          ? () => {
                                              setItemId(item.id)
                                              setModalClose(true)
                                              setIsCatalogUnlinkMode(false)
                                              initializeSelectedCatalogProducts(item, false)
                                              setOpenProductModel(true)
                                            }
                                          : null,
                                      Iccon: !item?.design_order_product_id && (isImage || isPdf) ? <Link /> : null,
                                      modalClose: modalClose,
                                    } : null
                                  }
                                  unlinkImageWithTask={
                                    item?.design_order_product_id && !isClient ? {
                                      name:
                                        item?.design_order_product_id && (isImage || isPdf)
                                          ? 'Unlink with catalog'
                                          : '',
                                      function:
                                        item?.design_order_product_id && (isImage || isPdf)
                                          ? () => {
                                              setItemId(item.id)
                                              setModalClose(true)
                                              setIsCatalogUnlinkMode(true)
                                              initializeSelectedCatalogProducts(item, true)
                                              setOpenProductModel(true)
                                            }
                                          : null,
                                      Iccon: item?.design_order_product_id && (isImage || isPdf) ? <Link /> : null,
                                      modalClose: modalClose,
                                    } : null
                                  }
                                  onDelete={() =>
                                    handleDeleteDesign({
                                      id: item.id,
                                      userId: user?.user_id,
                                    })
                                  }
                                />
                              )}
                            </>
                          }
                      />
                      {openProductModel && itemId === item.id && (() => {
                        const modalProducts = getCatalogProductsForModal(item)
                        const selectedCount = modalProducts.filter((product) =>
                          isCatalogProductSelected(product),
                        ).length
                        return (
                        <Card
                          sx={{
                            position: 'absolute',
                            zIndex: 1000,
                            bottom: 5,
                            left: 5,
                            top: 20,
                            p: 2,
                            maxHeight: '600px',
                            overflowY: 'auto',
                            width: '280px',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2">
                              {isCatalogUnlinkMode ? 'Unlink Product' : 'Choose Product'}
                            </Typography>
                            <CancelOutlined
                              sx={{ cursor: 'pointer' }}
                              onClick={closeCatalogProductModal}
                            />
                          </Stack>

                          {/* {modalProducts.length > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1, mt: 1 }}>
                              <Checkbox
                                checked={
                                  modalProducts.length > 0 && selectedCount === modalProducts.length
                                }
                                indeterminate={
                                  selectedCount > 0 && selectedCount < modalProducts.length
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCatalogProducts(modalProducts)
                                  } else {
                                    setSelectedCatalogProducts([])
                                  }
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                  const allSelected =
                                    modalProducts.length > 0 && selectedCount === modalProducts.length
                                  setSelectedCatalogProducts(allSelected ? [] : modalProducts)
                                }}
                              >
                                Select All
                              </Typography>
                            </Box>
                          ) : null}

                          <Divider sx={{ marginBottom: 2 }} /> */}

                          <Stack spacing={1}>
                            {modalProducts.length > 0 ? (
                              modalProducts.map((product) => (
                                <Box
                                  key={getCatalogProductKey(product)}
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <Checkbox
                                    checked={isCatalogProductSelected(product)}
                                    onChange={(e) =>
                                      handleCatalogCheckboxChange(product, e.target.checked)
                                    }
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      cursor: 'pointer',
                                      color: product?.product_notes ? 'error.main' : 'inherit',
                                      wordBreak: 'break-word',
                                    }}
                                    onClick={() =>
                                      handleCatalogCheckboxChange(
                                        product,
                                        !isCatalogProductSelected(product),
                                      )
                                    }
                                  >
                                    {getCatalogProductLabel(product)}
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ textAlign: 'center', color: 'gray', padding: 2 }}
                              >
                                No products left
                              </Typography>
                            )}
                          </Stack>

                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                            <Button onClick={closeCatalogProductModal} sx={{ color: 'red' }}>
                              Close
                            </Button>
                            {modalProducts.length > 0 && (
                              <Button
                                onClick={() =>
                                  isCatalogUnlinkMode
                                    ? handleSubmitCatalogUnlink(item.id)
                                    : handleSubmitCatalogLink(item.id)
                                }
                                disabled={selectedCatalogProducts.length === 0}
                              >
                                {isCatalogUnlinkMode ? 'Unlink' : 'Submit'}
                              </Button>
                            )}
                          </Box>
                        </Card>
                        )
                      })()}
                      
                      <Modal
                          open={openReasonModal && itemId === item.id}
                          handleClose={() => {
                            setOpenReasonModal(false)
                          }}
                          modalTitle={`Reason for Design Rejection`}
                        >
                          
                              <Grid container justifyContent="center" alignItems="center">
                                <Grid item xs={12} >
                                  <TextField
                                    fullWidth
                                    label="Reason"
                                    value={reasonText}
                                    onChange={(e) => setReasonText(e.target.value)}
                                  />
                                  <Box
                                    sx={{
                                      mt: 3,
                                      display: 'flex',
                                      justifyContent: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <LoadingButton
                                      type="submit"
                                      variant="contained"
                                      onClick={()=>handleUpdateDesign({
                                        id: itemId,
                                        reason: reasonText,
                                        approved: 0,
                                      })}
                                      // loading={isSubmitting}
                                    >
                                      {' Save '}
                                    </LoadingButton>
                                    <Button
                                      variant="outlined"
                                      type="submit"
                                      onClick={() => {
                                        setOpenReasonModal(false)
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                </Grid>
                              </Grid>
                        </Modal>
                      </Box>
                    </ImageListItem>
                    )
                    })}
                </ImageList>
              : 
                <Stack alignItems={'center'} justifyContent={'center'} >
                  <Typography>
                    {activeTab === 'files' ? 'No Files Found' : 'No Designs Found'}
                  </Typography>
                </Stack>
              }
              </Scrollbar>
            </Box>
          ) : (
            ''
          )}
          </>
        )}
      </Stack>
      <Modal width={1000}
              open={openImageModal}
              handleClose={() => {
                setOpenImageModal(false);
              }}
              modalTitle={'Designs'}
              >
              <Stack spacing={2}>
                <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
                  <AttachmentDownloadIconButton
                    file={imageDesigns[currentImageIndex] ? { ...imageDesigns[currentImageIndex], url: imageDesigns[currentImageIndex]?.url || imageDesigns[currentImageIndex]?.name } : null}
                    fileIndex={currentImageIndex ?? 0}
                    hidden={isClient || userType === 3}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSendCurrentItemToEmail(imageDesigns[currentImageIndex])}
                    disabled={!imageDesigns || imageDesigns.length === 0 || currentImageIndex === null}
                    startIcon={<Icon icon={emailFillIcon} width={20} height={20} />}
                  >
                    Send to Email
                  </Button>
                </Box>
                <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  gap:'1rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
            <IconButton
              onClick={handlePrevious}
              disabled={currentImageIndex === 0}
              sx={{
                opacity: currentImageIndex === 0 ? 0.3 : 1,
                pointerEvents: currentImageIndex === 0 ? 'none' : 'auto',
              }}
            >
              <Icon
                icon={arrowLeftOutlined}
                width={24}
                height={24}
                color="#006097"
              />
            </IconButton>

            <Box
            component={'div'} 
              style={{
                width: '500px',
                height: '500px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <img
                src={imageDesigns[currentImageIndex]?.name}
                // src={`https://gratisography.com/wp-content/uploads/2025/01/gratisography-dog-vacation-1170x780.jpg`}
                alt={`Image ${currentImageIndex + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 8,
                }}
              />
            </Box>
            <IconButton
              onClick={handleNext}
              disabled={currentImageIndex === imageDesigns.length - 1}
              sx={{
                opacity: currentImageIndex === imageDesigns.length - 1 ? 0.3 : 1,
                pointerEvents: currentImageIndex === imageDesigns.length - 1 ? 'none' : 'auto',
              }}
            >
              <Icon
                icon={arrowRightOutlined}
                width={24}
                height={24}
                color="#006097"
              />
            </IconButton>
          </div>
          </Stack>
        </Modal>

      <Modal width={1000}
        open={openImageModalReference}
        handleClose={() => {
          setOpenImageModalReference(false);
        }}
        modalTitle={'References'}
      >
        <Stack spacing={2}>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <AttachmentDownloadIconButton
              file={referenceFiles[currentImageIndex] ? { ...referenceFiles[currentImageIndex], url: referenceFiles[currentImageIndex]?.url || referenceFiles[currentImageIndex]?.name } : null}
              fileIndex={currentImageIndex ?? 0}
              hidden={isClient || userType === 3}
            />
          </Box>
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              gap:'1rem',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <IconButton
              onClick={handlePrevious}
              disabled={currentImageIndex === 0}
              sx={{
                opacity: currentImageIndex === 0 ? 0.3 : 1,
                pointerEvents: currentImageIndex === 0 ? 'none' : 'auto',
              }}
            >
              <Icon
                icon={arrowLeftOutlined}
                width={24}
                height={24}
                color="#006097"
              />
            </IconButton>

            <Box
              component={'div'} 
              style={{
                width: '500px',
                height: '500px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <img
                src={referenceFiles[currentImageIndex]?.name}
                // src={`https://gratisography.com/wp-content/uploads/2025/01/gratisography-dog-vacation-1170x780.jpg`}
                alt={`Image ${currentImageIndex + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 8,
                }}
              />
            </Box>
            <IconButton
              onClick={handleNext}
              disabled={currentImageIndex === imageDesigns.length - 1}
              sx={{
                opacity: currentImageIndex === imageDesigns.length - 1 ? 0.3 : 1,
                pointerEvents: currentImageIndex === imageDesigns.length - 1 ? 'none' : 'auto',
              }}
            >
              <Icon
                icon={arrowRightOutlined}
                width={24}
                height={24}
                color="#006097"
              />
            </IconButton>
          </div>
        </Stack>
      </Modal>
        <AttachmentPreviewDialog
          open={openPDFModal}
          onClose={() => {
            setOpenPDFModal(false)
            setSelectedPDFItem(null)
          }}
          fileUrl={selectedPDF}
          file={selectedPDFItem ? { ...selectedPDFItem, url: selectedPDFItem.url || selectedPDFItem.name } : null}
          isClient={isClient}
          userType={userType}
          pdfLoading={pdfLoading}
          setPdfLoading={setPdfLoading}
          useOfficeViewer={useOfficeViewer}
          setUseOfficeViewer={setUseOfficeViewer}
          headerStart={
            activeTabNew === 'designs' && selectedPDFItem ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSendCurrentItemToEmail(selectedPDFItem)}
                disabled={!selectedPDFItem}
                startIcon={<Icon icon={emailFillIcon} width={20} height={20} />}
                sx={{ fontSize: '14px', mr: 'auto' }}
              >
                Send to Email
              </Button>
            ) : null
          }
        />
        <SendDesignsEmailModal
          open={openEmailModal}
          onClose={() => setOpenEmailModal(false)}
          selectedDesigns={emailModalDesigns}
          designOrderId={details?.id}
          userId={user.user_id}
          userType={userType}
          onSuccess={handleEmailSent}
          pageType="design"
        />
      {selectedPDFForEdit && (
        <PDFEditor
          open={openPDFEditor}
          onClose={() => {
            setOpenPDFEditor(false);
            setSelectedPDFForEdit(null);
          }}
          pdfUrl={selectedPDFForEdit.url}
          attachmentId={selectedPDFForEdit.item?.id}
          requestId={details?.id}
          documentSource="design_order"
          onSaveSuccess={fetchRequestDetails}
        />
      )}
      {/* {(user.isVendor === null || user.isVendor === 0) &&(
      <AccountNotifications
                  isRequest={true}
                  requestId={requestId}
                 signAgreements={signAgreements}
                 customTemplate={customTemplate}
                 questionnaireTemplate={questionnaireTemplate}
                 allDataEmpty={allDataEmpty}
                 portfolio={portfolio}
                 updatingFunction={fetchRequestDetails}/>
                 )} */}
    </Card>
  )
}